/* UPDATE: assets/picco-pdp-upgrade.js - revision 4.2 - cart drawer and header count sync
 * Progressive enhancement for the Concept/Picco markup in the supplied export.
 * Never replaces the main product form, serializes its data, or adds a second
 * main-product cart handler. The sticky bar forwards to the original controls.
 */
(() => {
  'use strict';
  if (customElements.get('picco-pdp-upgrade')) return;

  let instanceCounter = 0;
  const cleanText = (node) => (node?.textContent || '').replace(/\s+/g, ' ').trim();
  const isVisible = (node) => Boolean(node?.isConnected && node.getClientRects().length &&
    getComputedStyle(node).visibility !== 'hidden' && !node.closest('[hidden]'));
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const button = (className, text) => {
    const node = element('button', className, text);
    node.type = 'button';
    return node;
  };
  const numberSetting = (value, fallback, min, max) => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(min, Math.min(max, number)) : fallback;
  };
  const sameOriginURL = (path) => {
    const url = new URL(path, location.href);
    if (url.origin !== location.origin || !/^https?:$/.test(url.protocol)) {
      throw new Error('Product and cart links must use this store.');
    }
    return url;
  };
  const money = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency: currency || 'AUD'
      }).format(Number(cents) / 100);
    } catch (_) {
      return `${currency || 'AUD'} ${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  // V4.2: Observe successful cart operations; never submit or retry an add.
  // The existing form/app remains the sole owner of the purchase request.
  const acquireCartUI = (owner) => {
    const key = '__piccoPdpCartUI42';
    if (!window[key]) window[key] = new PiccoCartUI();
    window[key].owners.add(owner);
    return window[key];
  };

  class PiccoCartUI {
    constructor() {
      this.owners = new Set();
      this.epoch = 0;
      this.inflight = 0;
      this.closeEpoch = 0;
      this.xhrRequests = new WeakMap();
      this.source = 'picco-pdp-cart-ui-4.2';
      const active = () => this.owners.size > 0;

      // Capture also receives non-bubbling events dispatched on document.
      window.addEventListener('ajaxProduct:added', (event) => {
        if (active()) this.added(event.detail?.product);
      }, true);

      window.addEventListener('cart:updated', (event) => {
        if (!active() || event.detail?.source === this.source) return;
        const cart = event.detail?.cart;

        if (this.isCart(cart)) {
          if (this.running) this.nativeAcknowledged = true;

          // The current refresh obtains a fresh cart after outstanding writes.
          if (!this.inflight && !this.running) this.paintCount(cart);
        } else if (!this.running) {
          this.queue(false);
        }
      }, true);

      window.addEventListener('pageshow', (event) => {
        if (active() && event.persisted) this.queue(false);
      });

      document.addEventListener('click', (event) => {
        if (!active()) return;

        const target = event.target instanceof Element ? event.target : null;
        const close = target?.closest(
          '#CartDrawer .drawer__close, #CartDrawer [data-drawer-close], #CartDrawer overlay-element[aria-controls="CartDrawer"]'
        );

        if (close) {
          this.closeEpoch++;
          this.wantOpen = false;

          const drawer = document.getElementById('CartDrawer');

          if (drawer?.dataset.pcuCartFallback === 'true') {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.closeFallback(drawer);
          }
        }
      }, true);

      document.addEventListener('keydown', (event) => {
        if (!active()) return;

        const drawer = document.getElementById('CartDrawer');

        if (!drawer || drawer.hidden || drawer.dataset.pcuCartFallback !== 'true') return;

        if (event.key === 'Escape') {
          event.preventDefault();

          this.closeEpoch++;
          this.wantOpen = false;
          this.closeFallback(drawer);
        } else if (event.key === 'Tab') {
          const nodes = [
            ...drawer.querySelectorAll(
              'button, a[href], input, select, textarea, [tabindex]'
            )
          ].filter(
            (node) =>
              !node.disabled &&
              node.tabIndex >= 0 &&
              isVisible(node)
          );

          const first = nodes[0];
          const last = nodes[nodes.length - 1];

          if (!first) {
            event.preventDefault();
            drawer.focus();
            return;
          }

          if (
            event.shiftKey &&
            (
              document.activeElement === first ||
              !drawer.contains(document.activeElement)
            )
          ) {
            event.preventDefault();
            last.focus();
          } else if (
            !event.shiftKey &&
            (
              document.activeElement === last ||
              !drawer.contains(document.activeElement)
            )
          ) {
            event.preventDefault();
            first.focus();
          }
        }
      }, true);

      this.installNetworkObservers();
    }

    isCart(cart) {
      return Boolean(
        cart &&
        Array.isArray(cart.items) &&
        Number.isInteger(cart.item_count) &&
        cart.item_count >= 0
      );
    }

    mutationType(input, init) {
      try {
        const request =
          typeof Request !== 'undefined' &&
          input instanceof Request;

        const method = String(
          init?.method ||
          (request ? input.method : 'GET')
        ).toUpperCase();

        if (method !== 'POST') return null;

        const url = new URL(
          request ? input.url : String(input),
          location.href
        );

        if (url.origin !== location.origin) return null;

        return url.pathname.match(
          /\/cart\/(add|change|update|clear)(?:\.js|\.json)?\/?$/
        )?.[1] || null;
      } catch (_) {
        return null;
      }
    }

    installNetworkObservers() {
      const bridge = this;
      const originalFetch = window.fetch;

      if (typeof originalFetch === 'function') {
        window.fetch = function (...args) {
          const type = bridge.owners.size
            ? bridge.mutationType(args[0], args[1])
            : null;

          if (type) {
            bridge.inflight++;
            bridge.epoch++;
          }

          let promise;

          try {
            promise = originalFetch.apply(this, args);
          } catch (error) {
            if (type) bridge.finished(type, false, null);
            throw error;
          }

          if (type) {
            // Return the ORIGINAL promise/response. Only a clone is inspected;
            // request headers, body, credentials, errors and aborts are untouched.
            promise.then(
              (response) => {
                if (!response.ok) {
                  bridge.finished(type, false, null);
                  return;
                }

                let copy;

                try {
                  copy = response.clone();
                } catch (_) {
                  bridge.finished(type, false, null);
                  return;
                }

                copy.json().then(
                  (data) => bridge.finished(type, true, data),
                  () => bridge.finished(type, false, null)
                );
              },
              () => bridge.finished(type, false, null)
            );
          }

          return promise;
        };
      }

      if (!window.XMLHttpRequest) return;

      const proto = XMLHttpRequest.prototype;
      const originalOpen = proto.open;
      const originalSend = proto.send;

      proto.open = function (method, url, ...rest) {
        bridge.xhrRequests.set(this, { method, url });
        return originalOpen.call(this, method, url, ...rest);
      };

      proto.send = function (...args) {
        const request = bridge.xhrRequests.get(this);

        const type =
          bridge.owners.size && request
            ? bridge.mutationType(
                request.url,
                { method: request.method }
              )
            : null;

        let settled = false;

        const complete = (ok, data) => {
          if (settled) return;

          settled = true;
          bridge.finished(type, ok, data);
        };

        if (type) {
          bridge.inflight++;
          bridge.epoch++;

          this.addEventListener(
            'loadend',
            () => {
              let data = null;

              try {
                data =
                  this.responseType === 'json'
                    ? this.response
                    : JSON.parse(this.responseText);
              } catch (_) {}

              complete(
                this.status >= 200 &&
                this.status < 300,
                data
              );
            },
            { once: true }
          );
        }

        try {
          return originalSend.apply(this, args);
        } catch (error) {
          if (type) complete(false, null);
          throw error;
        }
      };
    }

    finished(type, ok, data) {
      this.inflight = Math.max(0, this.inflight - 1);

      if (
        this.owners.size &&
        ok &&
        data &&
        !data.errors &&
        !(Number(data.status) >= 400)
      ) {
        if (type === 'add') {
          const lines = Array.isArray(data.items)
            ? data.items
            : [data];

          if (
            lines.some(
              (line) =>
                (line.variant_id || line.id) &&
                Number(line.quantity) > 0
            )
          ) {
            this.added(data);
          }
        } else if (this.isCart(data)) {
          this.queue(false);
        }
      }

      if (this.pending && !this.inflight) {
        this.schedule();
      }
    }

    added(data) {
      if (!this.owners.size) return;

      const lines = Array.isArray(data?.items)
        ? data.items
        : data
          ? [data]
          : [];

      const signature = lines.length
        ? JSON.stringify(
            lines.map(
              (line) => [
                line.key ||
                line.variant_id ||
                line.id,
                line.quantity
              ]
            )
          )
        : '';

      // Coalesce a native success event and the observer for that SAME add.
      // Multiple writes still lead to a fresh authoritative cart read.
      if (
        Date.now() - (this.lastAddAt || 0) < 600 &&
        (
          !signature ||
          signature === this.lastAddSignature
        )
      ) {
        if (this.running) this.queue(false);
        return;
      }

      this.lastAddAt = Date.now();
      this.lastAddSignature = signature;

      this.queue(true);
    }

    queue(open) {
      this.pending = true;
      this.wantOpen = Boolean(
        this.wantOpen ||
        open
      );
      this.epoch++;
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);

      if (
        !this.running &&
        !this.inflight &&
        this.owners.size
      ) {
        this.timer = setTimeout(
          () => this.flush(),
          50
        );
      }
    }

    async getJSON(url) {
      const controller = new AbortController();
      const timeout = setTimeout(
        () => controller.abort(),
        10000
      );

      try {
        const response = await fetch(
          url,
          {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: {
              Accept: 'application/json'
            },
            signal: controller.signal
          }
        );

        if (!response.ok) {
          throw new Error(
            `Cart display request failed (${response.status})`
          );
        }

        return await response.json();
      } finally {
        clearTimeout(timeout);
      }
    }

    cartURL() {
      const base = sameOriginURL(
        window.Shopify?.routes?.root || '/'
      );

      return new URL(
        `${base.pathname.replace(/\/?$/, '/')}cart.js`,
        base.origin
      ).href;
    }

    async renderedDrawer(drawer) {
      if (!drawer) return null;

      const section =
        drawer.getAttribute('data-section-id') ||
        drawer.dataset.sectionId ||
        drawer
          .closest('[id^="shopify-section-"]')
          ?.id
          ?.replace('shopify-section-', '') ||
        '';

      // Some Concept cart drawers are refreshed completely by the theme and
      // do not expose a usable section ID. That is not an error.
      if (!section) return null;

      const url = new URL(location.href);

      url.hash = '';
      url.searchParams.delete('section_id');
      url.searchParams.set(
        'sections',
        section
      );

      try {
        const result = await this.getJSON(
          url.href
        );

        const html = result?.[section];

        if (
          typeof html !== 'string' ||
          !html.trim()
        ) {
          return null;
        }

        return new DOMParser()
          .parseFromString(
            html,
            'text/html'
          )
          .getElementById('CartDrawer');
      } catch (error) {
        console.warn(
          '[Picco cart UI] Section refresh unavailable; native cart refresh will be used.',
          error
        );

        return null;
      }
    }

    paintCount(cart) {
      if (!this.isCart(cart)) return;

      const count = cart.item_count;

      document
        .querySelectorAll(
          '.picco-ref-header-wrap .ref-cart-count'
        )
        .forEach((node) => {
          if (
            node.textContent !==
            String(count)
          ) {
            node.textContent = String(count);
          }

          node.classList.toggle(
            'is-zero',
            count === 0
          );
        });

      document
        .querySelectorAll(
          '.picco-ref-header-wrap .picco-ref-header__cart'
        )
        .forEach((node) => {
          node.setAttribute(
            'aria-label',
            `Cart, ${count} ${
              count === 1
                ? 'item'
                : 'items'
            }`
          );
        });
    }

    patchPanel(drawer, fresh) {
      // Preserve the original drawer, overlay, inner wrapper and close button.
      // The supplied custom swipe/scroll-lock script holds references to them.
      const panel = drawer?.querySelector(
        '.drawer__panel'
      );

      const incoming = panel?.id
        ? fresh?.querySelector(
            `[id="${CSS.escape(panel.id)}"]`
          )
        : null;

      const next =
        incoming ||
        fresh?.querySelector(
          '.drawer__panel'
        );

      if (!panel || !next) {
        return false;
      }

      const nodes = [
        ...next.childNodes
      ].map(
        (node) =>
          document.importNode(
            node,
            true
          )
      );

      panel.replaceChildren(
        ...nodes
      );

      return true;
    }

    reveal(drawer) {
      if (!drawer) return false;

      for (
        const controller of this.owners
      ) {
        if (
          controller.quick?.dialog.open
        ) {
          controller.quick.dialog.close();
        }
      }

      const wasClosed =
        drawer.hasAttribute('hidden');

      if (wasClosed) {
        this.returnFocus =
          document.activeElement;
      }

      let nativeOpen = false;

      if (
        typeof drawer.show === 'function'
      ) {
        try {
          drawer.show();
          nativeOpen = true;
        } catch (_) {}
      }

      // This store's drawer CSS requires [active], not merely removal of hidden.
      drawer.removeAttribute('inert');
      drawer.removeAttribute('hidden');
      drawer.setAttribute(
        'active',
        ''
      );
      drawer.setAttribute(
        'aria-hidden',
        'false'
      );

      drawer.dataset.pcuCartVisible =
        'true';

      if (!nativeOpen) {
        drawer.dataset.pcuCartFallback =
          'true';
      } else {
        delete drawer.dataset
          .pcuCartFallback;
      }

      if (!this.visibilityStyle) {
        this.visibilityStyle =
          element('style');

        this.visibilityStyle.textContent = `
          #CartDrawer[data-pcu-cart-visible="true"][active]:not([hidden]) .drawer__content,
          #CartDrawer[data-pcu-cart-visible="true"][active]:not([hidden]) .drawer__panel {
            visibility: visible !important;
            opacity: 1 !important;
          }

          #CartDrawer[data-pcu-cart-visible="true"][active]:not([hidden]) > overlay-element {
            visibility: visible !important;
            opacity: 1 !important;
          }
        `;

        document.head.append(
          this.visibilityStyle
        );
      }

      if (
        this.observedDrawer !== drawer
      ) {
        this.drawerObserver?.disconnect();

        this.observedDrawer =
          drawer;

        this.drawerObserver =
          new MutationObserver(
            () => {
              if (drawer.hidden) {
                drawer.removeAttribute(
                  'active'
                );
              }

              this.syncExpanded(
                drawer
              );
            }
          );

        this.drawerObserver.observe(
          drawer,
          {
            attributes: true,
            attributeFilter: [
              'hidden',
              'active'
            ]
          }
        );
      }

      this.syncExpanded(drawer);

      if (
        wasClosed &&
        !nativeOpen
      ) {
        if (
          !drawer.hasAttribute(
            'tabindex'
          )
        ) {
          drawer.tabIndex = -1;
        }

        (
          drawer.querySelector(
            '.drawer__close'
          ) ||
          drawer
        ).focus(
          {
            preventScroll: true
          }
        );
      }

      for (
        const controller of this.owners
      ) {
        controller.scheduleView();
      }

      return true;
    }

    syncExpanded(drawer) {
      const open =
        !drawer.hidden &&
        drawer.hasAttribute('active');

      document
        .querySelectorAll(
          '.picco-ref-header__cart[aria-controls="CartDrawer"]'
        )
        .forEach((node) => {
          node.setAttribute(
            'aria-expanded',
            String(open)
          );
        });
    }

    closeFallback(drawer) {
      drawer.removeAttribute(
        'active'
      );

      drawer.setAttribute(
        'hidden',
        ''
      );

      drawer.setAttribute(
        'aria-hidden',
        'true'
      );

      this.syncExpanded(drawer);

      if (
        this.returnFocus?.isConnected
      ) {
        this.returnFocus.focus({
          preventScroll: true
        });
      }
    }

    notice(message) {
      console.warn(
        '[Picco cart UI]',
        message
      );

      for (
        const controller of this.owners
      ) {
        if (
          !controller.info?.isConnected
        ) {
          continue;
        }

        let node =
          controller.info.querySelector(
            '[data-pcu-cart-notice]'
          );

        if (!node) {
          node = controller.own(
            element(
              'p',
              'pcu-purchase-status'
            )
          );

          node.dataset.pcuCartNotice =
            '';

          node.setAttribute(
            'role',
            'status'
          );

          node.style.order = '51';

          controller.info.append(
            node
          );
        }

        const link = element(
          'a',
          '',
          'View cart'
        );

        const base = sameOriginURL(
          window.Shopify?.routes?.root ||
          '/'
        );

        link.href = new URL(
          `${base.pathname.replace(
            /\/?$/,
            '/'
          )}cart`,
          base.origin
        ).href;

        link.style.textDecoration =
          'underline';

        node.replaceChildren(
          document.createTextNode(
            message + ' '
          ),
          link
        );
      }
    }

    async flush() {
      if (
        this.running ||
        this.inflight ||
        !this.pending ||
        !this.owners.size
      ) {
        return;
      }

      this.running = true;
      this.pending = false;

      const open = Boolean(
        this.wantOpen
      );

      this.wantOpen = false;

      const epoch = this.epoch;
      const closeEpoch =
        this.closeEpoch;

      this.nativeAcknowledged =
        false;

      const drawer =
        document.getElementById(
          'CartDrawer'
        );

      try {
        if (
          open &&
          drawer
        ) {
          for (
            const owner of this.owners
          ) {
            if (
              owner.quick?.dialog.open
            ) {
              owner.quick.dialog.close();
            }
          }

          // Bubble FROM html: both the supplied header's html listener and
          // Concept's documented document listener receive the SAME event.
          document.documentElement
            .dispatchEvent(
              new CustomEvent(
                'cart:refresh',
                {
                  bubbles: true,
                  detail: {
                    open: true,
                    source: this.source
                  }
                }
              )
            );
        }

        const [
          cartResult,
          sectionResult
        ] =
          await Promise.allSettled(
            [
              this.getJSON(
                this.cartURL()
              ),
              open && drawer
                ? this.renderedDrawer(
                    drawer
                  )
                : Promise.resolve(
                    null
                  )
            ]
          );

        if (!this.owners.size) {
          return;
        }

        // A newer cart write began during our GETs: discard stale HTML/counts.
        if (
          epoch !== this.epoch ||
          this.inflight
        ) {
          this.pending = true;

          this.wantOpen =
            this.wantOpen ||
            (
              open &&
              closeEpoch ===
                this.closeEpoch
            );

          return;
        }

        const cartOk =
          cartResult.status ===
            'fulfilled' &&
          this.isCart(
            cartResult.value
          );

        if (cartOk) {
          this.paintCount(
            cartResult.value
          );

          // The authoritative cart request succeeded, so remove any old false warning.
          for (
            const owner of this.owners
          ) {
            owner.info
              ?.querySelector(
                '[data-pcu-cart-notice]'
              )
              ?.remove();
          }
        }

        if (
          open &&
          closeEpoch ===
            this.closeEpoch
        ) {
          const current =
            document.getElementById(
              'CartDrawer'
            );

          const fresh =
            sectionResult.status ===
              'fulfilled'
              ? sectionResult.value
              : null;

          const patched =
            Boolean(
              current &&
              fresh &&
              this.patchPanel(
                current,
                fresh
              )
            );

          const alreadyOpen =
            Boolean(
              current &&
              !current.hasAttribute(
                'hidden'
              ) &&
              (
                current.hasAttribute(
                  'active'
                ) ||
                current.hasAttribute(
                  'open'
                ) ||
                isVisible(current)
              )
            );

          // There are three valid success paths:
          // 1. Concept acknowledged cart:refresh.
          // 2. Picco successfully patched fresh drawer HTML.
          // 3. Concept already opened/refreshed the drawer itself.
          //
          // A successful /cart.js response confirms the cart itself is fine,
          // so failure to independently verify the drawer is NOT shown to
          // the customer as an error.
          if (
            current &&
            (
              this.nativeAcknowledged ||
              patched ||
              alreadyOpen
            )
          ) {
            this.reveal(current);

            for (
              const owner of this.owners
            ) {
              owner.info
                ?.querySelector(
                  '[data-pcu-cart-notice]'
                )
                ?.remove();
            }
          } else if (!cartOk) {
            this.notice(
              'The cart display could not be refreshed.'
            );
          } else {
            console.warn(
              '[Picco cart UI] Cart updated successfully; native drawer refresh was not independently confirmed.'
            );
          }
        }
      } catch (_) {
        if (open) {
          this.notice(
            'Your item was added, but the cart display could not be refreshed.'
          );
        }
      } finally {
        this.running = false;

        if (this.pending) {
          this.schedule();
        }
      }
    }
  }

  class PiccoProductUpgrade extends HTMLElement {
    connectedCallback() {
      if (this.life) return;

      this.uid =
        `pcu-${++instanceCounter}`;

      this.life =
        new AbortController();

      this.records =
        new Map();

      this.owned =
        new Set();

      this.moves =
        new Map();

      this.panels = [];
      this.sizeGrids =
        new Map();

      this.readInventory();

      this.frame = 0;
      this.viewFrame = 0;

      this.on(
        document,
        'DOMContentLoaded',
        () => this.connectProduct()
      );

      this.on(
        document,
        'shopify:section:load',
        () => this.connectProduct()
      );

      this.on(
        document,
        'shopify:section:unload',
        (event) => {
          if (
            this.root &&
            event.target.contains(
              this.root
            )
          ) {
            this.teardownProduct();
          }
        }
      );

      this.on(
        document,
        'shopify:block:select',
        (event) => {
          const index =
            this.panels.findIndex(
              ({ detail }) =>
                detail.contains(
                  event.target
                )
            );

          if (index >= 0) {
            this.selectTab(
              index,
              false
            );
          }
        }
      );

      this.connectProduct();

      // The controller is intentionally placed before the product section.
      if (
        !this.root &&
        this.dataset.enabled !==
          'false'
      ) {
        this.waitObserver =
          new MutationObserver(
            () =>
              this.connectProduct()
          );

        this.waitObserver.observe(
          document.body,
          {
            childList: true,
            subtree: true
          }
        );

        this.waitTimer =
          setTimeout(
            () =>
              this.waitObserver
                ?.disconnect(),
            15000
          );
      }
    }

    disconnectedCallback() {
      this.waitObserver
        ?.disconnect();

      clearTimeout(
        this.waitTimer
      );

      this.teardownProduct();

      this.life?.abort();
      this.life = null;
    }

    on(
      target,
      name,
      handler,
      options = {}
    ) {
      target?.addEventListener(
        name,
        handler,
        {
          ...options,
          signal: this.life.signal
        }
      );
    }

    own(node) {
      node.setAttribute(
        'data-pcu-owned',
        this.uid
      );

      this.owned.add(node);

      return node;
    }

    remember(
      node,
      kind,
      key,
      value
    ) {
      if (
        !this.records.has(node)
      ) {
        this.records.set(
          node,
          new Map()
        );
      }

      const records =
        this.records.get(node);

      const recordKey =
        `${kind}:${key}`;

      if (
        !records.has(recordKey)
      ) {
        records.set(
          recordKey,
          {
            kind,
            key,
            value
          }
        );
      }
    }

    attr(
      node,
      name,
      value
    ) {
      if (!node) return;

      const next =
        value === null
          ? null
          : String(value);

      if (
        node.getAttribute(name) ===
        next
      ) {
        return;
      }

      this.remember(
        node,
        'attr',
        name,
        node.getAttribute(name)
      );

      if (next === null) {
        node.removeAttribute(name);
      } else {
        node.setAttribute(
          name,
          next
        );
      }
    }

    style(
      node,
      name,
      value
    ) {
      if (
        node.style.getPropertyValue(
          name
        ) === value
      ) {
        return;
      }

      this.remember(
        node,
        'style',
        name,
        [
          node.style.getPropertyValue(
            name
          ),
          node.style.getPropertyPriority(
            name
          )
        ]
      );

      node.style.setProperty(
        name,
        value
      );
    }

    addClass(
      node,
      name
    ) {
      if (
        !node ||
        node.classList.contains(
          name
        )
      ) {
        return;
      }

      this.remember(
        node,
        'class',
        name,
        false
      );

      node.classList.add(name);
    }

    restoreNode(node) {
      const records =
        this.records.get(node);

      if (!records) return;

      for (
        const {
          kind,
          key,
          value
        } of records.values()
      ) {
        if (
          kind === 'attr'
        ) {
          if (value === null) {
            node.removeAttribute(
              key
            );
          } else {
            node.setAttribute(
              key,
              value
            );
          }
        } else if (
          kind === 'style'
        ) {
          if (value[0]) {
            node.style.setProperty(
              key,
              value[0],
              value[1]
            );
          } else {
            node.style.removeProperty(
              key
            );
          }
        } else if (
          kind === 'class'
        ) {
          node.classList.remove(
            key
          );
        }
      }

      this.records.delete(node);
    }

    // Relocate existing nodes, never clone forms, app widgets or their handlers.
    moveNode(
      node,
      destination
    ) {
      if (
        !node ||
        !destination ||
        node.parentNode ===
          destination
      ) {
        return;
      }

      if (
        !this.moves.has(node)
      ) {
        const anchor =
          document.createComment(
            'Picco original position'
          );

        node.before(anchor);

        this.moves.set(
          node,
          anchor
        );
      }

      destination.append(node);
    }

    restoreMoves() {
      for (
        const [
          node,
          anchor
        ] of this.moves
      ) {
        if (
          anchor.parentNode
        ) {
          anchor.replaceWith(
            node
          );
        }
      }

      this.moves.clear();
    }

    removeStaleMoves() {
      for (
        const [
          node,
          anchor
        ] of this.moves
      ) {
        if (
          !this.root.contains(
            anchor
          )
        ) {
          node.remove();
          anchor.remove();

          this.restoreNode(node);

          this.moves.delete(
            node
          );

          if (
            this.modelCaption ===
            node
          ) {
            this.modelCaption =
              null;
          }
        }
      }
    }

    connectProduct() {
      if (
        !this.isConnected ||
        this.dataset.enabled ===
          'false'
      ) {
        return;
      }

      const candidates = [
        ...document.querySelectorAll(
          'main product-info[data-product-id]'
        )
      ];

      const info =
        candidates.find(
          (node) =>
            node.dataset.productId ===
              this.dataset.productId &&
            !node.closest(
              'dialog, x-modal, quick-view, quick-add-modal, [data-quickshop-dialog]'
            )
        );

      const root =
        info?.closest(
          '[id^="shopify-section-"]'
        );

      if (
        !root ||
        !root.querySelector(
          '.featured-product.product'
        )
      ) {
        return;
      }

      if (
        this.root === root &&
        this.info === info
      ) {
        return;
      }

      if (
        root.hasAttribute(
          'data-pcu-root'
        ) &&
        root.getAttribute(
          'data-pcu-root'
        ) !== this.uid
      ) {
        return;
      }

      if (this.root) {
        this.teardownProduct();
      }

      this.waitObserver
        ?.disconnect();

      clearTimeout(
        this.waitTimer
      );

      this.root = root;
      this.info = info;

      this.attr(
        root,
        'data-pcu-root',
        this.uid
      );

      this.attr(
        root,
        'data-pcu-thumbnails',
        this.dataset.thumbnails ===
          'true'
      );

      this.attr(
        root,
        'data-pcu-breadcrumbs',
        this.dataset.breadcrumbs ===
          'true'
      );

      this.style(
        root,
        '--pcu-mobile-reserve',
        `${numberSetting(
          this.dataset.mobileReserve,
          154,
          100,
          250
        )}px`
      );

      this.style(
        root,
        '--pcu-header-offset',
        `${numberSetting(
          this.dataset.headerOffset,
          80,
          0,
          160
        )}px`
      );

      this.style(
        root,
        '--pcu-desktop-font',
        `${numberSetting(
          this.dataset.desktopFont,
          11,
          10,
          16
        )}px`
      );

      this.style(
        root,
        '--pcu-mobile-font',
        `${numberSetting(
          this.dataset.mobileFont,
          10,
          10,
          16
        )}px`
      );

      this.style(
        root,
        '--pcu-image-fit',
        this.dataset.fit ===
          'contain'
          ? 'contain'
          : 'cover'
      );

      this.productLife =
        new AbortController();

      this.cartUI =
        acquireCartUI(this);

      const listen = (
        node,
        name,
        handler,
        options = {}
      ) =>
        node.addEventListener(
          name,
          handler,
          {
            ...options,
            signal:
              this.productLife
                .signal
          }
        );

      listen(
        window,
        'change',
        (event) =>
          this.localPickerChange(
            event
          ),
        {
          capture: true
        }
      );

      listen(
        window,
        'popstate',
        () => {
          if (!this.localMode) {
            return;
          }

          const id =
            new URL(
              location.href
            ).searchParams.get(
              'variant'
            );

          const entry =
            this.localById.get(
              String(id)
            );

          if (entry) {
            this.commitLocalVariant(
              entry.variant.options,
              {
                media: true
              }
            );
          }
        }
      );

      listen(
        root,
        'change',
        (event) => {
          if (
            !this.info.contains(
              event.target
            )
          ) {
            return;
          }

          if (
            event.target.matches(
              'variant-selects input[type="radio"], variant-selects select'
            )
          ) {
            this.beginVariantChange();
          }

          this.scheduleRefresh();
        }
      );

      // Keep the main product form and app handlers. React only to a confirmed
      // successful native add; a click or a failed POST never opens the drawer.
      listen(
        root,
        'click',
        (event) => {
          const submit =
            event.target.closest?.(
              '.product-form__submit'
            );

          const accelerated =
            event.target.closest?.(
              '.shopify-payment-button'
            );

          if (
            this.localMode &&
            (
              submit ||
              accelerated
            ) &&
            this.form?.contains(
              submit ||
              accelerated
            ) &&
            !this.localPurchaseAllowed()
          ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }

          if (
            submit &&
            this.form?.contains(
              submit
            ) &&
            this.stickyPending
          ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.layoutInventory();
            return;
          }

          if (
            submit &&
            this.form?.contains(
              submit
            ) &&
            !this.ctaBlocked(
              submit
            )
          ) {
            this.cartIntent = {
              at: Date.now(),
              variant:
                this.form.elements
                  .namedItem('id')
                  ?.value
            };
          }
        },
        {
          capture: true
        }
      );

      listen(
        root,
        'submit',
        (event) => {
          if (
            event.target ===
              this.form &&
            (
              this.stickyPending ||
              !this.localPurchaseAllowed()
            )
          ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.layoutInventory();
            return;
          }

          if (
            event.target ===
            this.form
          ) {
            this.cartIntent = {
              at: Date.now(),
              variant:
                this.form.elements
                  .namedItem('id')
                  ?.value
            };
          }
        },
        {
          capture: true
        }
      );

      // Cart UI observes successful responses as well as native success events.
      // It does not rely on a time-limited click record to open the drawer.
      listen(
        document,
        'ajaxProduct:error',
        () => {
          this.cartIntent = null;
        }
      );

      listen(
        document,
        'variant:change',
        () =>
          this.scheduleRefresh()
      );

      listen(
        document,
        'pointerdown',
        (event) => {
          for (
            const state of
            this.sizeGrids.values()
          ) {
            if (
              !state.grid.contains(
                event.target
              )
            ) {
              state.restoreFocus =
                false;
            }
          }
        },
        {
          capture: true,
          passive: true
        }
      );

      listen(
        root,
        'click',
        (event) => {
          if (
            this.info.contains(
              event.target
            )
          ) {
            this.scheduleRefresh();
          }
        }
      );

      listen(
        root,
        'click',
        (event) => {
          const trigger =
            event.target.closest?.(
              '.pcu-zoom-trigger'
            );

          if (
            trigger &&
            matchMedia(
              '(min-width: 1024px)'
            ).matches
          ) {
            event.preventDefault();

            this.openZoom(
              trigger.closest(
                '.main-img-wrap'
              )
            );
          }
        }
      );

      if (
        this.dataset.quickshop ===
        'true'
      ) {
        // Capture prevents the old malformed productUrl + '.js' handler firing too.
        listen(
          root,
          'click',
          (event) => {
            const trigger =
              event.target.closest?.(
                '[data-picco-style-with-root] [data-quick-add][data-product-url]'
              );

            if (
              !trigger ||
              !root.contains(
                trigger
              ) ||
              !(
                'HTMLDialogElement'
                in window
              )
            ) {
              return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            this.openQuickShop(
              trigger
            );
          },
          {
            capture: true
          }
        );
      }

      listen(
        window,
        'scroll',
        () =>
          this.scheduleView(),
        {
          passive: true,
          capture: true
        }
      );

      listen(
        window,
        'resize',
        () => {
          this.scheduleRefresh();
          this.scheduleView();
        },
        {
          passive: true
        }
      );

      if (
        window.visualViewport
      ) {
        listen(
          window.visualViewport,
          'resize',
          () =>
            this.scheduleView(),
          {
            passive: true
          }
        );
      }

      listen(
        root,
        'scroll',
        () =>
          this.scheduleView(),
        {
          passive: true,
          capture: true
        }
      );

      listen(
        root,
        'transitionend',
        () =>
          this.scheduleView()
      );

      this.refresh();

      this.productObserver =
        new MutationObserver(
          (records) => {
            // Reattach the SAME visible size grid during the mutation microtask,
            // before a replacement native fieldset can paint a flashing empty row.
            if (
              records.some(
                (record) =>
                  record.type ===
                    'childList' &&
                  [
                    ...record.addedNodes,
                    ...record.removedNodes
                  ].some(
                    (node) =>
                      node.nodeType ===
                        1 &&
                      (
                        node.matches?.(
                          'variant-selects, fieldset, .swatches, product-info'
                        ) ||
                        node.querySelector?.(
                          'variant-selects, fieldset[data-option-slug]'
                        )
                      )
                  )
              )
            ) {
              this.resolveInfo();
              this.syncSizeGrids();
            }

            // Ignore our UI, not changes made by Timesact or the native product component.
            if (
              records.some(
                (record) => {
                  const target =
                    record.target
                      .nodeType === 1
                      ? record.target
                      : record.target
                          .parentElement;

                  if (
                    !target ||
                    target.closest(
                      '.pcu-progress, .pcu-sticky, .pcu-dialog'
                    )
                  ) {
                    return false;
                  }

                  if (
                    record.type ===
                    'childList'
                  ) {
                    return [
                      ...record.addedNodes,
                      ...record.removedNodes
                    ].some(
                      (node) =>
                        node.nodeType ===
                          1 &&
                        !node.hasAttribute(
                          'data-pcu-owned'
                        )
                    );
                  }

                  return (
                    this.info.contains(
                      target
                    ) &&
                    !target.closest(
                      '[data-pcu-owned]'
                    )
                  );
                }
              )
            ) {
              this.scheduleRefresh();
            }
          }
        );

      this.productObserver.observe(
        root,
        {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: [
            'disabled',
            'aria-disabled',
            'aria-busy',
            'value',
            'checked',
            'hidden',
            'class',
            'style'
          ]
        }
      );

      this.resizeObserver =
        typeof ResizeObserver ===
          'function'
          ? new ResizeObserver(
              () =>
                this.scheduleView()
            )
          : null;

      this.resizeObserver?.observe(
        root
      );

      const drawer =
        document.getElementById(
          'CartDrawer'
        );

      if (drawer) {
        this.drawerObserver =
          new MutationObserver(
            () =>
              this.scheduleView()
          );

        this.drawerObserver.observe(
          drawer,
          {
            attributes: true,
            attributeFilter: [
              'hidden',
              'open',
              'active',
              'aria-hidden'
            ]
          }
        );
      }

      this.scheduleView();
    }

    scheduleRefresh() {
      if (
        !this.root ||
        this.frame
      ) {
        return;
      }

      this.frame =
        requestAnimationFrame(
          () => {
            this.frame = 0;

            if (
              this.root
                ?.isConnected
            ) {
              this.refresh();
            }
          }
        );
    }

    resolveInfo() {
      if (
        this.info?.isConnected &&
        this.root?.contains(
          this.info
        )
      ) {
        return true;
      }

      const next = [
        ...(
          this.root
            ?.querySelectorAll(
              'product-info'
            ) ||
          []
        )
      ].find(
        (node) =>
          node.dataset.productId ===
          this.dataset.productId
      );

      if (!next) return false;

      this.resetTabs();
      this.info = next;

      return true;
    }

    refresh() {
      if (
        !this.resolveInfo()
      ) {
        return;
      }

      this.removeStaleMoves();

      this.form =
        this.info.querySelector(
          'form[data-type="add-to-cart-form"]'
        );

      this.setupLocalVariants();
      this.layoutBlocks();

      this.info
        .querySelectorAll(
          'variant-selects fieldset'
        )
        .forEach(
          (fieldset) => {
            const count =
              fieldset.querySelectorAll(
                'input[type="radio"]'
              ).length;

            if (count) {
              this.style(
                fieldset,
                '--pcu-option-count',
                String(
                  Math.min(
                    8,
                    count
                  )
                )
              );
            }

            const name =
              fieldset.dataset
                .optionSlug ||
              cleanText(
                fieldset.querySelector(
                  'legend'
                )
              ) ||
              fieldset.querySelector(
                'input[type="radio"]'
              )?.name ||
              '';

            if (
              /size|waist|length/i.test(
                name
              )
            ) {
              this.attr(
                fieldset,
                'data-pcu-size-field',
                ''
              );
            }
          }
        );

      this.syncSizeGrids();
      this.syncVariantPending();
      this.syncLocalPurchaseState();
      this.layoutInventory();

      if (
        this.dataset.tabs ===
        'true'
      ) {
        this.buildTabs();
      }

      if (
        this.dataset.sticky ===
          'true' &&
        this.form
      ) {
        this.buildSticky();
      }

      this.buildComplementary();
      this.buildGallery();

      if (
        this.dataset.zoom ===
          'true' &&
        'HTMLDialogElement'
          in window
      ) {
        this.buildZoomTriggers();
      }

      this.syncSticky();
      this.scheduleView();
    }

    layoutBlocks() {
      [
        ...this.info.children
      ].forEach(
        (node) => {
          if (
            /^(SCRIPT|STYLE|NOSCRIPT)$/.test(
              node.tagName
            ) ||
            node.hasAttribute(
              'data-pcu-owned'
            )
          ) {
            return;
          }

          let order = 70;

          if (
            node.matches(
              '.product__title'
            )
          ) {
            order = 10;
          } else if (
            node.matches(
              '.product__price'
            )
          ) {
            order = 20;
          } else if (
            node.matches(
              'variant-selects'
            )
          ) {
            order = 40;
          } else if (
            node.matches(
              '.product-form-wrapper'
            )
          ) {
            order = 50;
          } else if (
            node.matches(
              this.inventorySelector()
            )
          ) {
            order = 45;
          } else if (
            node.querySelector(
              '.custom-breadcrumbs'
            )
          ) {
            order = 0;

            this.addClass(
              node,
              'pcu-breadcrumb-block'
            );
          } else if (
            node.querySelector(
              'scu-option'
            )
          ) {
            order = 30;

            this.addClass(
              node,
              'pcu-swatch-block'
            );
          } else if (
            node.querySelector(
              'square-placement, afterpay-placement'
            )
          ) {
            order = 60;

            this.addClass(
              node,
              'pcu-payment-block'
            );
          } else if (
            node.matches(
              '.picco-omnisend-shell'
            )
          ) {
            order = 65;
          } else if (
            node.matches(
              'details.product__accordion'
            )
          ) {
            order = 81;
          } else if (
            node.matches(
              '[data-picco-style-with-root]'
            )
          ) {
            order = 100;
          }

          this.attr(
            node,
            'data-pcu-order',
            ''
          );

          this.style(
            node,
            '--pcu-order',
            String(order)
          );
        }
      );
    }

    openCartDrawer() {
      if (
        this.quick?.dialog.open
      ) {
        this.quick.dialog.close();
      }

      this.cartUI?.added();
      this.scheduleView();

      return Boolean(
        document.getElementById(
          'CartDrawer'
        )
      );
    }

    // Revision 4. All variant data is emitted by Liquid with the first HTML
    // response. No fetch, option-value pub/sub event or native input.click()
    // occurs on the local selection path.
    setupLocalVariants() {
      if (
        this.localAttempted ||
        !this.form
      ) {
        return;
      }

      this.localAttempted =
        true;

      const data =
        this.inventory;

      const records =
        data?.variants;

      const valid =
        data?.schemaVersion === 4 &&
        data.complete === true &&
        data.localCompatible ===
          true &&
        Array.isArray(
          records
        ) &&
        records.length > 0 &&
        records.length ===
          Number(
            data.variantCount
          ) &&
        records.every(
          (item) =>
            item.variant &&
            String(item.id) ===
              String(
                item.variant.id
              ) &&
            Array.isArray(
              item.variant
                .options
            ) &&
            typeof item.variant
              .available ===
              'boolean' &&
            Number.isFinite(
              Number(
                item.variant
                  .price
              )
            )
        );

      if (!valid) {
        this.attr(
          this.root,
          'data-pcu-variant-mode',
          'native-fallback'
        );

        console.warn(
          '[Picco PDP v4] Local variant data is missing, incomplete or incompatible. ' +
          'Update the Liquid section too. Combined listings, volume-pricing and >250 variants retain the native path.'
        );

        return;
      }

      this.localById =
        new Map(
          records.map(
            (item) => [
              String(item.id),
              item
            ]
          )
        );

      this.localByOptions =
        new Map(
          records.map(
            (item) => [
              JSON.stringify(
                item.variant
                  .options
              ),
              item
            ]
          )
        );

      this.localMode = true;

      this.attr(
        this.root,
        'data-pcu-variant-mode',
        'local'
      );

      this.attr(
        this.root,
        'data-pcu-revision',
        '4.2'
      );

      this.initialPurchaseLabel =
        cleanText(
          this.form.querySelector(
            '.product-form__submit .btn-text'
          )
        );

      this.initialPreorder =
        /pre[\s-]?order/i.test(
          this.initialPurchaseLabel
        );

      this.masterVariantInputs()
        .forEach(
          (input) => {
            if (
              input.tagName !==
              'SELECT'
            ) {
              return;
            }

            // Populate an existing master variant select once, not on every click.
            for (
              const item of records
            ) {
              if (
                ![
                  ...input.options
                ].some(
                  (option) =>
                    option.value ===
                    String(
                      item.id
                    )
                )
              ) {
                const option =
                  document.createElement(
                    'option'
                  );

                option.value =
                  String(item.id);

                option.textContent =
                  item.variant.title;

                input.append(option);
              }
            }
          }
        );

      const id =
        this.form.elements
          .namedItem('id')
          ?.value;

      const initial =
        this.localById.get(
          String(id)
        );

      const values =
        this.nativeOptions();

      this.localValues =
        initial?.variant
          .options
          .slice() ||
        values;

      this.commitLocalVariant(
        this.localValues,
        {
          notify: false,
          media: false
        }
      );
    }

    masterVariantInputs() {
      const forms = [
        ...(
          this.info
            ?.querySelectorAll(
              'form'
            ) ||
          []
        )
      ].filter(
        (form) =>
          form === this.form ||
          form.classList.contains(
            'installment'
          )
      );

      return [
        ...new Set(
          forms.flatMap(
            (form) =>
              [
                ...form.elements
              ].filter(
                (input) =>
                  input.name ===
                    'id' &&
                  /^(INPUT|SELECT)$/.test(
                    input.tagName
                  )
              )
          )
        )
      ];
    }

    localPickerChange(event) {
      if (!this.localMode) {
        return;
      }

      const input =
        event.target;

      if (
        !(
          input instanceof Element
        ) ||
        !this.info.contains(
          input
        ) ||
        !input.matches(
          'variant-selects input[type="radio"], variant-selects select'
        ) ||
        input.matches(
          '[name="id"], [name="selling_plan"]'
        ) ||
        input.closest(
          'dialog, x-modal'
        )
      ) {
        return;
      }

      // Block the old native "change -> fetch -> replace picker" handler only.
      // Do not block form IDs, selling plans, quantity, cart or app events.
      event.stopImmediatePropagation();

      if (
        this.notifyingLocal
      ) {
        // An app may echo a radio change while handling our form-ID event.
        // Keep that echo out of the old reload path and retain the user's choice.
        this.syncLocalNativeOptions();
        return;
      }

      this.commitLocalVariant(
        this.nativeOptions()
      );
    }

    commitLocalVariant(
      options,
      {
        notify = true,
        media = true
      } = {}
    ) {
      if (
        !this.localMode ||
        !Array.isArray(options)
      ) {
        return false;
      }

      const nextValues =
        options.map(
          (value) =>
            String(value)
        );

      const entry =
        this.localByOptions.get(
          JSON.stringify(
            nextValues
          )
        ) ||
        null;

      const previous =
        this.localEntry
          ?.variant ||
        null;

      const previousEntry =
        this.localEntry;

      const changed =
        JSON.stringify(
          this.localValues
        ) !==
          JSON.stringify(
            nextValues
          ) ||
        String(
          previous?.id ||
          ''
        ) !==
          String(
            entry?.variant
              .id ||
            ''
          );

      this.localValues =
        nextValues;

      this.localEntry =
        entry;

      this.stickyPending =
        false;

      this.variantFailed =
        false;

      clearTimeout(
        this.pendingTimer
      );

      this.syncLocalNativeOptions();
      this.syncLocalFormIds();
      this.syncLocalVariantJSON();
      this.updateLocalQuantityRules();
      this.syncSizeGrids();
      this.syncLocalPurchaseState();
      this.layoutInventory();
      this.syncSticky();

      if (
        media &&
        changed &&
        entry?.mediaId &&
        entry.mediaId !==
          previousEntry?.mediaId
      ) {
        this.showLocalVariantMedia(
          entry.mediaId
        );
      }

      if (
        notify &&
        changed
      ) {
        if (
          this.info.dataset
            .updateUrl !==
          'false'
        ) {
          const url =
            new URL(
              location.href
            );

          url.searchParams.delete(
            'option_values'
          );

          if (entry) {
            url.searchParams.set(
              'variant',
              String(
                entry.id
              )
            );
          } else {
            url.searchParams.delete(
              'variant'
            );
          }

          history.replaceState(
            history.state,
            '',
            url.href
          );
        }

        this.notifyLocalVariant(
          previous
        );
      }

      // App listeners may synchronously change the selling plan or the button.
      // Recheck without rebuilding any option, product-info, form or gallery node.
      this.syncLocalPurchaseState();
      this.syncSticky();

      return Boolean(entry);
    }

    syncLocalNativeOptions() {
      const fields = [
        ...this.info.querySelectorAll(
          'variant-selects fieldset'
        )
      ];

      fields.forEach(
        (
          field,
          position
        ) => {
          const key =
            field.dataset
              .optionIndex ||
            '';

          const index =
            /^option\d+$/.test(
              key
            )
              ? Number(
                  key.replace(
                    'option',
                    ''
                  )
                ) - 1
              : position;

          const value =
            this.localValues[
              index
            ];

          field
            .querySelectorAll(
              'input[type="radio"]'
            )
            .forEach(
              (input) => {
                const exists =
                  this.inventory
                    .variants
                    .some(
                      (item) =>
                        item.variant
                          .options[
                          index
                        ] ===
                        input.value
                    );

                const values =
                  this.localValues
                    .slice();

                values[index] =
                  input.value;

                const item =
                  this.localByOptions
                    .get(
                      JSON.stringify(
                        values
                      )
                    );

                input.checked =
                  input.value ===
                  value;

                // Sold-out choices remain selectable for stock/notify information.
                // Never substitute a different available variant behind the customer's back.
                input.disabled =
                  !exists;

                input.classList.toggle(
                  'disabled',
                  !item?.variant
                    .available
                );
              }
            );

          field
            .querySelectorAll(
              'select'
            )
            .forEach(
              (select) => {
                if (
                  select.closest(
                    'x-modal, dialog'
                  )
                ) {
                  return;
                }

                if (
                  select.value !==
                    value &&
                  [
                    ...select.options
                  ].some(
                    (option) =>
                      option.value ===
                      value
                  )
                ) {
                  select.value =
                    value;
                }

                for (
                  const option of
                  select.options
                ) {
                  if (
                    !option.value
                  ) {
                    continue;
                  }

                  option.disabled =
                    !this.inventory
                      .variants
                      .some(
                        (item) =>
                          item.variant
                            .options[
                            index
                          ] ===
                          option.value
                      );
                }
              }
            );

          const output =
            field.querySelector(
              '.form__label [id$="-option' +
              (index + 1) +
              '"], .form__label [data-selected-value]'
            );

          if (
            output &&
            output.textContent !==
              value
          ) {
            output.textContent =
              value ||
              '';
          }
        }
      );
    }

    syncLocalFormIds() {
      const variant =
        this.localEntry
          ?.variant;

      for (
        const input of
        this.masterVariantInputs()
      ) {
        const value =
          variant
            ? String(
                variant.id
              )
            : '';

        if (
          input.value !==
          value
        ) {
          input.value = value;
        }

        if (
          input.tagName ===
            'INPUT' &&
          input.getAttribute(
            'value'
          ) !== value
        ) {
          input.setAttribute(
            'value',
            value
          );
        }

        input.disabled =
          !variant;
      }
    }

    syncLocalVariantJSON() {
      const variant =
        this.localEntry
          ?.variant ||
        null;

      const json =
        this.info.querySelector(
          'variant-selects [data-selected-variant]'
        );

      if (json) {
        const text =
          JSON.stringify(
            variant
          );

        if (
          json.textContent !==
          text
        ) {
          json.textContent =
            text;
        }
      }

      // Some native versions keep an own-property cache; never override a
      // prototype getter/setter or a theme method with an invented interface.
      [
        this.info,
        this.info.querySelector(
          'variant-selects'
        ),
        this.form
      ]
        .filter(Boolean)
        .forEach(
          (node) => {
            const property =
              Object.getOwnPropertyDescriptor(
                node,
                'currentVariant'
              );

            if (
              property?.writable
            ) {
              node.currentVariant =
                variant;
            }
          }
        );
    }

    notifyLocalVariant(
      previous
    ) {
      this.notifyingLocal =
        true;

      try {
        // Hidden form-ID changes notify purchase/app integrations. They are NOT
        // radio changes and cannot enter variant-selects' change listener.
        this.masterVariantInputs()
          .forEach(
            (input) => {
              input.dispatchEvent(
                new Event(
                  'change',
                  {
                    bubbles: true
                  }
                )
              );
            }
          );

        this.form.dispatchEvent(
          new CustomEvent(
            'variant:change',
            {
              bubbles: true,
              detail: {
                variant:
                  this.localEntry
                    ?.variant ||
                  null,
                previousVariant:
                  previous,
                productId:
                  this.dataset
                    .productId,
                sectionId:
                  this.info.dataset
                    .sectionId,
                source:
                  'picco-pdp-upgrade-local'
              }
            }
          )
        );
      } finally {
        this.notifyingLocal =
          false;
      }
    }

    localPlanState() {
      const raw =
        this.localEntry
          ?.variant;

      if (
        !raw ||
        !this.form
      ) {
        return {
          id: '',
          allocation: null,
          issue: ''
        };
      }

      const inputs = [
        ...this.form.elements
      ].filter(
        (input) =>
          input.name ===
          'selling_plan'
      );

      if (
        inputs.some(
          (input) =>
            input.hasAttribute(
              'data-timesact-selling-plan'
            )
        )
      ) {
        this.timesactPlanSeen =
          true;
      }

      const active =
        inputs.filter(
          (input) =>
            !input.disabled &&
            (
              !/^(radio|checkbox)$/.test(
                input.type
              ) ||
              input.checked
            )
        );

      const ids = [
        ...new Set(
          active
            .map(
              (input) =>
                input.value
            )
            .filter(Boolean)
        )
      ];

      const id =
        ids[0] ||
        '';

      const allocations =
        raw.selling_plan_allocations ||
        [];

      const allocation =
        allocations.find(
          (item) =>
            String(
              item.selling_plan_id
            ) ===
            String(id)
        ) ||
        null;

      const requires =
        raw.requires_selling_plan ||
        (
          this.timesactPlanSeen &&
          allocations.length >
            0
        );

      let issue = '';

      if (
        ids.length > 1 ||
        (
          id &&
          !allocation
        ) ||
        (
          requires &&
          !id
        )
      ) {
        issue =
          'Choose a valid purchase option for this size before adding to cart.';
      }

      // Do not invent a selling plan, silently change payment terms, or remove
      // a Timesact field. The existing app owns the choice; invalid IDs block purchase.
      return {
        id,
        allocation,
        issue
      };
    }

    syncLocalPurchaseState() {
      if (
        !this.localMode ||
        !this.form
      ) {
        return;
      }

      const variant =
        this.localEntry
          ?.variant ||
        null;

      const plan =
        this.localPlanState();

      this.localPlanIssue =
        variant?.available
          ? plan.issue
          : '';

      if (!this.planStatus) {
        this.planStatus =
          this.own(
            element(
              'p',
              'pcu-purchase-status'
            )
          );

        this.planStatus.setAttribute(
          'role',
          'status'
        );

        this.planStatus.style.order =
          '46';

        this.planStatus.hidden =
          true;

        this.info.append(
          this.planStatus
        );
      }

      if (
        this.planStatus
          .textContent !==
        this.localPlanIssue
      ) {
        this.planStatus.textContent =
          this.localPlanIssue;
      }

      this.planStatus.hidden =
        !this.localPlanIssue;

      const strings =
        window.theme
          ?.variantStrings ||
        {};

      const submit =
        this.form.querySelector(
          '.product-form__submit'
        );

      const busy =
        this.localAdding ||
        submit?.classList.contains(
          'loading'
        ) ||
        submit?.getAttribute(
          'aria-busy'
        ) === 'true' ||
        this.form.getAttribute(
          'aria-busy'
        ) === 'true';

      if (
        submit &&
        !busy
      ) {
        const blocked =
          !variant?.available ||
          Boolean(
            this.localPlanIssue
          );

        const desired =
          !variant
            ? (
                strings.unavailable ||
                'Unavailable'
              )
            : !variant.available
              ? (
                  strings.soldOut ||
                  'Sold out'
                )
              : (
                  (
                    this.initialPreorder &&
                    (
                      plan.id ||
                      this.localEntry
                        .state ===
                        'order'
                    )
                  )
                    ? (
                        strings.preOrder ||
                        this.initialPurchaseLabel ||
                        'Pre-order'
                      )
                    : (
                        strings.addToCart ||
                        'Add to cart'
                      )
                );

        const signature =
          JSON.stringify(
            [
              variant?.id,
              variant?.available,
              plan.id,
              this.localPlanIssue,
              desired
            ]
          );

        // Do not repeatedly overwrite a button owned by a running preorder app.
        if (
          this.purchaseSignature !==
          signature
        ) {
          submit.disabled =
            blocked;

          // A presence-based native/app guard also treats aria-disabled="false"
          // as a lock. Remove it when enabled instead of writing the string false.
          // This branch is skipped while the native form/button is busy, so an
          // in-flight request keeps its existing lock and cannot be submitted twice.
          if (blocked) {
            submit.setAttribute(
              'aria-disabled',
              'true'
            );
          } else {
            submit.removeAttribute(
              'aria-disabled'
            );
          }

          const textNodes =
            submit.querySelectorAll(
              '.btn-text, .btn-fill'
            );

          textNodes.forEach(
            (node) => {
              if (
                node.textContent !==
                desired
              ) {
                node.textContent =
                  desired;
              }
            }
          );

          this.purchaseSignature =
            signature;
        }
      }

      this.updateLocalPrice(
        plan
      );
    }

    updateLocalPrice(plan) {
      const record =
        this.localEntry;

      const area =
        this.info.querySelector(
          '.product__price [id^="Price-"] .price, .product__price .price'
        );

      if (!area) return;

      this.attr(
        area,
        'data-pcu-price-unavailable',
        record
          ? null
          : ''
      );

      if (!record) return;

      if (
        this.livePrice?.area !==
        area
      ) {
        const current =
          area.querySelector(
            '.price__regular, .price__sale'
          ) ||
          this.own(
            element(
              'span',
              'price__regular'
            )
          );

        if (
          !current.isConnected
        ) {
          area.append(current);
        }

        this.attr(
          current,
          'data-pcu-price-current',
          ''
        );

        area.querySelectorAll(
          '.price__regular, .price__sale, .price__compare, .price__unit'
        ).forEach(
          (node) => {
            if (
              node !== current &&
              !current.contains(
                node
              )
            ) {
              this.attr(
                node,
                'data-pcu-price-suppressed',
                ''
              );
            }
          }
        );

        const compare =
          this.own(
            element(
              's',
              'pcu-price-compare'
            )
          );

        const unit =
          this.own(
            element(
              'small',
              'pcu-price-unit'
            )
          );

        compare.hidden = true;
        unit.hidden = true;

        area.append(
          compare,
          unit
        );

        this.livePrice = {
          area,
          current,
          compare,
          unit
        };
      }

      const allocation =
        record.plans?.find(
          (item) =>
            String(item.id) ===
            String(plan.id)
        );

      const amount =
        allocation
          ? allocation.price
          : record.variant
              .price;

      const compareAmount =
        allocation
          ? allocation.compareAtPrice
          : record.variant
              .compare_at_price;

      const priceText =
        allocation
          ? allocation.priceText
          : record.priceText;

      const compareText =
        allocation
          ? allocation.compareText
          : record.compareText;

      if (
        this.livePrice.current
          .textContent !==
        priceText
      ) {
        this.livePrice.current
          .textContent =
          priceText;
      }

      if (
        this.livePrice.compare
          .textContent !==
        compareText
      ) {
        this.livePrice.compare
          .textContent =
          compareText;
      }

      this.livePrice.compare.hidden =
        !(
          Number(compareAmount) >
          Number(amount)
        );

      const measure =
        record.variant
          .unit_price_measurement;

      const unitText =
        measure
          ? `${record.unitText} / ${
              Number(
                measure.reference_value
              ) === 1
                ? ''
                : measure.reference_value
            }${measure.reference_unit}`
          : '';

      if (
        this.livePrice.unit
          .textContent !==
        unitText
      ) {
        this.livePrice.unit
          .textContent =
          unitText;
      }

      this.livePrice.unit.hidden =
        !unitText;

      this.info
        .querySelectorAll(
          'square-placement, afterpay-placement'
        )
        .forEach(
          (placement) => {
            const value =
              (
                Number(amount) /
                100
              ).toFixed(2);

            if (
              placement.getAttribute(
                'data-amount'
              ) !== value
            ) {
              placement.setAttribute(
                'data-amount',
                value
              );
            }
          }
        );
    }

    updateLocalQuantityRules() {
      const record =
        this.localEntry;

      if (!record) return;

      const rule =
        record.quantityRule ||
        record.variant
          .quantity_rule ||
        {
          min: 1,
          increment: 1,
          max: null
        };

      for (
        const input of [
          ...this.form.elements
        ].filter(
          (node) =>
            node.name ===
              'quantity' &&
            node.type ===
              'number'
        )
      ) {
        const min =
          Math.max(
            1,
            Number(rule.min) ||
            1
          );

        const step =
          Math.max(
            1,
            Number(
              rule.increment
            ) ||
            1
          );

        const max =
          rule.max === null ||
          rule.max === undefined
            ? null
            : Number(
                rule.max
              );

        input.min =
          String(min);

        input.step =
          String(step);

        input.setAttribute(
          'data-min',
          String(min)
        );

        if (max !== null) {
          input.max =
            String(max);

          input.setAttribute(
            'data-max',
            String(max)
          );
        } else {
          input.removeAttribute(
            'max'
          );

          input.removeAttribute(
            'data-max'
          );
        }

        const old =
          Math.max(
            min,
            Number(
              input.value
            ) ||
            min
          );

        let quantity =
          min +
          Math.ceil(
            (
              old -
              min
            ) /
            step
          ) *
            step;

        if (max !== null) {
          quantity =
            Math.max(
              min,
              Math.min(
                quantity,
                min +
                Math.floor(
                  (
                    max -
                    min
                  ) /
                  step
                ) *
                  step
              )
            );
        }

        input.value =
          String(quantity);
      }
    }

    localPurchaseAllowed() {
      if (!this.localMode) {
        return true;
      }

      this.syncLocalFormIds();
      this.syncLocalPurchaseState();

      return Boolean(
        this.localEntry
          ?.variant
          .available &&
        !this.localPlanIssue
      );
    }

    showLocalVariantMedia(
      mediaId
    ) {
      const rawId =
        String(mediaId);

      if (
        innerWidth < 1024 &&
        this.galleryTrack
      ) {
        const slides =
          this.gallerySlides();

        const index =
          slides.findIndex(
            (slide) =>
              (
                slide.dataset
                  .mediaId ||
                slide.id ||
                ''
              )
                .split('-')
                .pop() ===
              rawId
          );

        if (index < 0) {
          return;
        }

        if (
          this.galleryMode() ===
          'scroll'
        ) {
          this.goToGallerySlide(
            index
          );
        } else {
          const gallery =
            this.galleryTrack.closest(
              'media-gallery'
            );

          if (
            typeof gallery
              ?.setActiveMedia ===
            'function'
          ) {
            gallery.setActiveMedia(
              slides[index]
                .dataset
                .mediaId,
              false
            );
          }
        }
      }

      // On desktop the existing grid already contains the media. Do not scroll
      // the page, rebuild the grid or reset the caption for a size-only change.
    }

    readInventory() {
      try {
        const data =
          JSON.parse(
            this.querySelector(
              '[data-pcu-inventory]'
            )?.textContent ||
            'null'
          );

        this.inventory =
          data &&
          String(data.productId) ===
            String(
              this.dataset
                .productId
            ) &&
          Array.isArray(
            data.variants
          )
            ? data
            : null;
      } catch (_) {
        this.inventory =
          null;
      }
    }

    selectedVariant() {
      if (
        this.localMode
      ) {
        return (
          this.localEntry
            ?.variant ||
          null
        );
      }

      try {
        return JSON.parse(
          this.info
            ?.querySelector(
              'variant-selects [data-selected-variant]'
            )
            ?.textContent ||
          'null'
        );
      } catch (_) {
        return null;
      }
    }

    nativeOptions() {
      const values = [];

      this.info
        ?.querySelectorAll(
          'variant-selects fieldset, variant-selects .product-form__input'
        )
        .forEach(
          (field) => {
            // Nested field wrappers may repeat a field; positions are explicit in Concept.
            const raw =
              field.dataset
                .optionIndex ||
              '';

            const index =
              /option\d+/.test(
                raw
              )
                ? Number(
                    raw.replace(
                      'option',
                      ''
                    )
                  ) - 1
                : [
                    ...this.info.querySelectorAll(
                      'variant-selects fieldset'
                    )
                  ].indexOf(
                    field
                  );

            const control =
              field.querySelector(
                'input[type="radio"]:checked, select'
              );

            if (
              control &&
              index >= 0
            ) {
              values[index] =
                control.value;
            }
          }
        );

      return values;
    }

    stockForOptions(
      options
    ) {
      if (
        !this.inventory ||
        String(
          this.info.dataset
            .productId
        ) !==
          String(
            this.inventory
              .productId
          )
      ) {
        return null;
      }

      // No guessing when a product has additional unselected options or is outside
      // the Liquid payload's 250-variant page. Never mark another colour's stock.
      const matches =
        this.inventory
          .variants
          .filter(
            (variant) =>
              variant.options
                ?.length ===
                options.length &&
              variant.options
                .every(
                  (
                    value,
                    index
                  ) =>
                    value ===
                    options[index]
                )
          );

      return (
        matches.length === 1
          ? matches[0]
          : null
      );
    }

    beginVariantChange() {
      if (this.localMode) {
        return;
      }

      this.variantFailed =
        false;

      this.stickyPending =
        true;

      this.pendingSince =
        Date.now();

      this.syncSizeGrids();
      this.syncSticky();

      clearTimeout(
        this.pendingTimer
      );

      this.pendingTimer =
        setTimeout(
          () => {
            if (!this.root) {
              return;
            }

            this.syncVariantPending();

            if (
              this.stickyPending
            ) {
              // Do not unlock a sticky purchase against a stale hidden variant ID.
              this.variantFailed =
                true;

              this.layoutInventory();
            }
          },
          15000
        );
    }

    syncVariantPending() {
      if (
        !this.stickyPending
      ) {
        return;
      }

      const selected =
        this.selectedVariant();

      const values =
        this.nativeOptions();

      const input =
        this.form?.elements
          .namedItem('id');

      const matches =
        selected?.options
          ?.length ===
          values.length &&
        selected.options.every(
          (
            value,
            index
          ) =>
            value ===
            values[index]
        );

      if (
        matches &&
        (
          String(
            selected.id
          ) ===
            String(
              input?.value
            ) ||
          selected.available ===
            false
        )
      ) {
        this.stickyPending =
          false;

        this.variantFailed =
          false;

        clearTimeout(
          this.pendingTimer
        );
      }
    }

    syncSizeGrids() {
      if (
        !this.info
          ?.isConnected
      ) {
        return;
      }

      const fields = [
        ...this.info.querySelectorAll(
          'variant-selects fieldset'
        )
      ];

      const liveKeys =
        new Set();

      fields.forEach(
        (
          field,
          position
        ) => {
          const name =
            field.dataset
              .optionSlug ||
            cleanText(
              field.querySelector(
                ':scope > legend'
              )
            ) ||
            '';

          if (
            !/size|waist|length/i.test(
              name
            )
          ) {
            return;
          }

          const list =
            field.querySelector(
              ':scope > .swatches'
            );

          const inputs =
            list
              ? [
                  ...list.querySelectorAll(
                    'input[type="radio"]'
                  )
                ]
              : [];

          if (
            !inputs.length
          ) {
            return;
          }

          const key =
            field.dataset
              .optionIndex ||
            String(position);

          const optionIndex =
            /option\d+/.test(
              key
            )
              ? Number(
                  key.replace(
                    'option',
                    ''
                  )
                ) - 1
              : position;

          liveKeys.add(key);

          let state =
            this.sizeGrids.get(
              key
            );

          if (!state) {
            const grid =
              this.own(
                element(
                  'div',
                  'pcu-size-grid'
                )
              );

            grid.setAttribute(
              'role',
              'radiogroup'
            );

            grid.setAttribute(
              'aria-label',
              cleanText(
                field.querySelector(
                  ':scope > legend'
                )
              ) ||
              name
            );

            state = {
              key,
              grid,
              buttons:
                new Map(),
              optionIndex,
              restoreFocus:
                false
            };

            this.sizeGrids.set(
              key,
              state
            );

            this.on(
              grid,
              'click',
              (event) => {
                const cell =
                  event.target.closest(
                    '.pcu-size-option'
                  );

                if (
                  cell &&
                  grid.contains(
                    cell
                  )
                ) {
                  // Stop click delegation at the visible grid. It never clicks the
                  // native radio in local mode, so no native section reload follows.
                  event.preventDefault();
                  event.stopPropagation();

                  this.chooseSize(
                    state,
                    cell.dataset
                      .value
                  );
                }
              }
            );

            this.on(
              grid,
              'keydown',
              (event) => {
                if (
                  ![
                    'ArrowLeft',
                    'ArrowRight',
                    'ArrowUp',
                    'ArrowDown',
                    'Home',
                    'End'
                  ].includes(
                    event.key
                  )
                ) {
                  return;
                }

                event.preventDefault();

                const cells = [
                  ...grid.querySelectorAll(
                    '.pcu-size-option:not(:disabled)'
                  )
                ];

                const index =
                  cells.indexOf(
                    event.target.closest(
                      '.pcu-size-option'
                    )
                  );

                if (
                  !cells.length ||
                  index < 0
                ) {
                  return;
                }

                const rtl =
                  document.documentElement
                    .dir ===
                  'rtl';

                const forward =
                  event.key ===
                    'ArrowDown' ||
                  event.key ===
                    (
                      rtl
                        ? 'ArrowLeft'
                        : 'ArrowRight'
                    );

                const next =
                  event.key ===
                    'Home'
                    ? 0
                    : event.key ===
                        'End'
                      ? cells.length -
                        1
                      : (
                          index +
                          (
                            forward
                              ? 1
                              : -1
                          ) +
                          cells.length
                        ) %
                        cells.length;

                cells[next].focus({
                  preventScroll:
                    true
                });

                this.chooseSize(
                  state,
                  cells[next]
                    .dataset
                    .value
                );
              }
            );
          }

          state.field = field;
          state.inputs = inputs;
          state.optionIndex =
            optionIndex;

          const values =
            new Set(
              inputs.map(
                (input) =>
                  input.value
              )
            );

          for (
            const [
              value,
              cell
            ] of state.buttons
          ) {
            if (
              !values.has(
                value
              )
            ) {
              cell.remove();

              state.buttons.delete(
                value
              );
            }
          }

          const current =
            this.nativeOptions();

          const checked =
            inputs.find(
              (input) =>
                input.checked
            );

          inputs.forEach(
            (
              input,
              index
            ) => {
              let cell =
                state.buttons.get(
                  input.value
                );

              if (!cell) {
                cell = button(
                  'pcu-size-option'
                );

                cell.dataset.value =
                  input.value;

                cell.setAttribute(
                  'role',
                  'radio'
                );

                const text =
                  element(
                    'span',
                    'pcu-size-value',
                    input.value
                  );

                const dot =
                  element(
                    'span',
                    'pcu-low-stock-dot'
                  );

                dot.setAttribute(
                  'aria-hidden',
                  'true'
                );

                dot.hidden =
                  true;

                cell.append(
                  text,
                  dot
                );

                state.buttons.set(
                  input.value,
                  cell
                );
              }

              if (
                state.grid.children[
                  index
                ] !== cell
              ) {
                state.grid.insertBefore(
                  cell,
                  state.grid.children[
                    index
                  ] ||
                  null
                );
              }

              const selected =
                input ===
                checked;

              cell.setAttribute(
                'aria-checked',
                String(selected)
              );

              cell.disabled =
                input.disabled;

              cell.tabIndex =
                selected ||
                (
                  !checked &&
                  index === 0
                )
                  ? 0
                  : -1;

              const options =
                current.slice();

              options[
                optionIndex
              ] = input.value;

              const stock =
                this.stockForOptions(
                  options
                );

              const low =
                this.dataset
                  .lowStock !==
                  'false' &&
                !input.disabled &&
                !input.classList.contains(
                  'disabled'
                ) &&
                stock?.available ===
                  true &&
                stock?.low ===
                  true;

              cell.querySelector(
                '.pcu-low-stock-dot'
              ).hidden =
                !low;

              cell.setAttribute(
                'aria-label',
                `${input.value}${
                  low
                    ? ', low stock'
                    : ''
                }`
              );

              cell.title =
                low
                  ? `${input.value} - Low stock`
                  : input.value;

              const unavailable =
                input.disabled ||
                input.classList.contains(
                  'disabled'
                ) ||
                stock?.available ===
                  false;

              cell.dataset.unavailable =
                String(
                  unavailable
                );
            }
          );

          this.attr(
            field,
            'data-pcu-size-field',
            ''
          );

          this.attr(
            field,
            'data-pcu-stable-size',
            ''
          );

          this.style(
            field,
            '--pcu-option-count',
            String(
              Math.min(
                8,
                inputs.length
              )
            )
          );

          if (
            state.grid.parentNode !==
            field
          ) {
            list.after(
              state.grid
            );
          }

          if (
            state.restoreFocus &&
            document.activeElement ===
              document.body &&
            state.focusValue
          ) {
            state.buttons
              .get(
                state.focusValue
              )
              ?.focus({
                preventScroll:
                  true
              });
          }
        }
      );

      for (
        const [
          key,
          state
        ] of this.sizeGrids
      ) {
        if (
          !liveKeys.has(key)
        ) {
          state.grid.remove();

          this.owned.delete(
            state.grid
          );

          this.sizeGrids.delete(
            key
          );
        }
      }
    }

    chooseSize(
      state,
      value
    ) {
      // Re-resolve the native input each time: Concept replaces its own inputs
      // when it fetches new availability. Never keep an obsolete input reference.
      const input =
        state.inputs.find(
          (item) =>
            item.value ===
              value &&
            item.isConnected
        );

      if (
        !input ||
        input.disabled ||
        input.checked
      ) {
        return;
      }

      state.focusValue =
        value;

      state.restoreFocus =
        true;

      if (this.localMode) {
        const options =
          this.localValues
            .slice();

        options[
          state.optionIndex
        ] = value;

        this.commitLocalVariant(
          options
        );

        return;
      }

      input.click();

      this.syncSizeGrids();
      this.syncSticky();
      this.scheduleRefresh();
    }

    inventorySelector() {
      return '.product__inventory, product-inventory, [id^="Inventory-"], [id^="inventory-"], ' +
        '[data-inventory-status], .inventory-status, .product-inventory';
    }

    layoutInventory() {
      const nodes = [
        ...this.info.querySelectorAll(
          this.inventorySelector()
        )
      ].filter(
        (node) =>
          !node.closest(
            'dialog, x-modal, [data-picco-style-with-root]'
          )
      );

      // Preserve the native status text, translations and live updates. Only
      // relocate its visual order when it is a separate product-info block.
      nodes.forEach(
        (node) => {
          this.attr(
            node,
            'data-pcu-stock-native',
            ''
          );

          let block = node;

          while (
            block.parentElement &&
            block.parentElement !==
              this.info &&
            !block.parentElement
              .matches(
                'form, .product-form-wrapper, variant-selects'
              )
          ) {
            block =
              block.parentElement;
          }

          if (
            block.parentElement ===
              this.info &&
            !block.matches(
              '.product-form-wrapper, variant-selects, .product__title, .product__price, details, .shopify-app-block'
            )
          ) {
            this.attr(
              block,
              'data-pcu-stock-block',
              ''
            );

            this.attr(
              block,
              'data-pcu-order',
              ''
            );

            this.style(
              block,
              '--pcu-order',
              '45'
            );
          }
        }
      );

      const native =
        !this.localMode &&
        nodes.some(
          (node) =>
            isVisible(node) &&
            cleanText(node)
        );

      const showFallback =
        this.dataset
          .stockMessage !==
          'false' &&
        Boolean(
          this.inventory
        );

      if (
        !this.stockStatus &&
        (
          showFallback ||
          this.variantFailed
        )
      ) {
        this.stockStatus =
          this.own(
            element(
              'p',
              'pcu-inventory-status'
            )
          );

        this.stockStatus.setAttribute(
          'role',
          'status'
        );

        this.stockStatus.setAttribute(
          'aria-live',
          'polite'
        );

        this.stockStatus.style.order =
          '45';
      }

      if (!this.stockStatus) {
        return;
      }

      if (
        this.stockStatus
          .parentNode !==
        this.info
      ) {
        this.info.append(
          this.stockStatus
        );
      }

      let text = '';

      const selected =
        this.selectedVariant();

      const item =
        this.inventory
          ?.variants
          .find(
            (v) =>
              String(v.id) ===
              String(
                selected?.id
              )
          );

      if (
        this.localMode &&
        !selected
      ) {
        text = 'Unavailable';
      } else if (
        this.variantFailed
      ) {
        text =
          'Size could not finish updating. Please select another size or reload the page.';
      } else if (
        this.stickyPending
      ) {
        text =
          'Updating availability...';
      } else if (
        item?.available ===
        false
      ) {
        text = 'Sold out';
      } else if (
        item?.low
      ) {
        text = 'Low stock';
      } else if (
        item?.state ===
        'stocked'
      ) {
        // Do not label an available preorder, untracked item or zero-stock
        // continue-selling variant as physically in stock.
        text = 'In stock';
      }

      if (
        this.stockStatus
          .textContent !==
        text
      ) {
        this.stockStatus.textContent =
          text;
      }

      this.stockStatus.hidden =
        !text ||
        (
          native &&
          !this.variantFailed
        ) ||
        (
          !showFallback &&
          !this.variantFailed
        );

      this.stockStatus.dataset.low =
        String(
          Boolean(
            item?.low
          ) &&
          !this.stickyPending
        );
    }

    buildTabs() {
      const details = [
        ...this.info.querySelectorAll(
          ':scope > details.product__accordion'
        )
      ].filter(
        (node) =>
          node.querySelector(
            ':scope > summary'
          ) &&
          node.querySelector(
            ':scope > .details__content'
          )
      );

      if (
        details.length < 2
      ) {
        return;
      }

      if (
        details.length ===
          this.panels.length &&
        details.every(
          (
            node,
            i
          ) =>
            this.panels[
              i
            ]?.detail ===
            node
        )
      ) {
        return;
      }

      const previousLabel =
        this.panels[
          this.activeTab ||
          0
        ]?.label;

      this.resetTabs();

      this.tablist =
        this.own(
          element(
            'div',
            'pcu-tabs'
          )
        );

      this.tablist.setAttribute(
        'role',
        'tablist'
      );

      this.tablist.setAttribute(
        'aria-label',
        'Product information'
      );

      this.tablist.style.order =
        '80';

      details[0].before(
        this.tablist
      );

      this.panels =
        details.map(
          (
            detail,
            index
          ) => {
            const summary =
              detail.querySelector(
                ':scope > summary'
              );

            const content =
              detail.querySelector(
                ':scope > .details__content'
              );

            const label =
              cleanText(
                summary.querySelector(
                  '.leading-none'
                ) ||
                summary
              );

            const tab =
              button(
                'pcu-tab',
                label
              );

            tab.id =
              `${this.uid}-tab-${index}`;

            tab.setAttribute(
              'role',
              'tab'
            );

            if (
              !content.id
            ) {
              this.attr(
                content,
                'id',
                `${this.uid}-panel-${index}`
              );
            }

            tab.setAttribute(
              'aria-controls',
              content.id
            );

            this.attr(
              content,
              'role',
              'tabpanel'
            );

            this.attr(
              content,
              'aria-labelledby',
              tab.id
            );

            this.attr(
              content,
              'tabindex',
              '0'
            );

            this.addClass(
              detail,
              'pcu-panel'
            );

            this.on(
              tab,
              'click',
              () =>
                this.selectTab(
                  index,
                  false
                )
            );

            this.on(
              tab,
              'keydown',
              (event) => {
                const keys = [
                  'ArrowLeft',
                  'ArrowRight',
                  'Home',
                  'End'
                ];

                if (
                  !keys.includes(
                    event.key
                  )
                ) {
                  return;
                }

                event.preventDefault();

                const last =
                  this.panels.length -
                  1;

                const direction =
                  document.documentElement
                    .dir ===
                  'rtl'
                    ? -1
                    : 1;

                let next =
                  index;

                if (
                  event.key ===
                  'Home'
                ) {
                  next = 0;
                }

                if (
                  event.key ===
                  'End'
                ) {
                  next = last;
                }

                if (
                  event.key ===
                  'ArrowRight'
                ) {
                  next =
                    (
                      index +
                      direction +
                      last +
                      1
                    ) %
                    (
                      last +
                      1
                    );
                }

                if (
                  event.key ===
                  'ArrowLeft'
                ) {
                  next =
                    (
                      index -
                      direction +
                      last +
                      1
                    ) %
                    (
                      last +
                      1
                    );
                }

                this.selectTab(
                  next,
                  true
                );
              }
            );

            this.tablist.append(
              tab
            );

            return {
              detail,
              content,
              tab,
              label
            };
          }
        );

      const previousIndex =
        this.panels.findIndex(
          (panel) =>
            panel.label ===
            previousLabel
        );

      this.selectTab(
        Math.max(
          0,
          previousIndex
        ),
        false
      );
    }

    selectTab(
      index,
      focus
    ) {
      this.activeTab =
        index;

      this.panels.forEach(
        (
          {
            detail,
            tab
          },
          i
        ) => {
          const selected =
            i === index;

          tab.setAttribute(
            'aria-selected',
            String(selected)
          );

          tab.tabIndex =
            selected
              ? 0
              : -1;

          this.attr(
            detail,
            'hidden',
            selected
              ? null
              : ''
          );

          this.attr(
            detail,
            'open',
            selected
              ? ''
              : null
          );

          this.attr(
            detail,
            'aria-expanded',
            String(selected)
          );
        }
      );

      if (focus) {
        this.panels[
          index
        ]?.tab.focus();
      }

      this.scheduleView();
    }

    resetTabs() {
      this.tablist?.remove();

      this.owned.delete(
        this.tablist
      );

      this.tablist = null;

      this.panels.forEach(
        (
          {
            detail,
            content
          }
        ) => {
          this.restoreNode(
            detail
          );

          this.restoreNode(
            content
          );
        }
      );

      this.panels = [];
    }

    nativeCTA() {
      if (!this.form) {
        return null;
      }

      const candidates = [
        ...this.form.querySelectorAll(
          '.product-form__submit, .timesact-button-bis, .timesact-button-cs'
        )
      ];

      return (
        candidates.find(
          isVisible
        ) ||
        null
      );
    }

    ctaBlocked(node) {
      return (
        !node ||
        node.disabled ||
        node.getAttribute(
          'aria-disabled'
        ) === 'true' ||
        node.getAttribute(
          'aria-busy'
        ) === 'true' ||
        node.classList.contains(
          'loading'
        ) ||
        this.form?.getAttribute(
          'aria-busy'
        ) === 'true' ||
        Boolean(
          this.stickyPending
        ) ||
        Boolean(
          this.localMode &&
          node?.matches(
            '.product-form__submit'
          ) &&
          (
            !this.localEntry
              ?.variant
              .available ||
            this.localPlanIssue
          )
        )
      );
    }

    buildSticky() {
      if (
        this.sticky
          ?.isConnected
      ) {
        return;
      }

      this.sticky =
        this.own(
          element(
            'div',
            'pcu-sticky'
          )
        );

      this.sticky.hidden =
        true;

      this.sticky.setAttribute(
        'role',
        'region'
      );

      this.sticky.setAttribute(
        'aria-label',
        'Product purchase controls'
      );

      this.stickyCTA =
        button(
          'pcu-button',
          'View product options'
        );

      this.on(
        this.stickyCTA,
        'click',
        () => {
          const target =
            this.nativeCTA();

          if (
            this.ctaBlocked(
              target
            )
          ) {
            return;
          }

          // Do NOT fetch cart/add here. This original button belongs to the native
          // form, Timesact, quantity, selling_plan and line-item property inputs.
          target.click();

          this.scheduleRefresh();

          clearTimeout(
            this.errorTimer
          );

          this.errorTimer =
            setTimeout(
              () => {
                const error =
                  this.form?.querySelector(
                    '[role="alert"]:not([hidden])'
                  );

                if (
                  error &&
                  cleanText(
                    error
                  )
                ) {
                  error.scrollIntoView(
                    {
                      block: 'center',
                      behavior:
                        'smooth'
                    }
                  );
                }
              },
              500
            );
        }
      );

      this.sticky.append(
        this.stickyCTA
      );

      document.body.append(
        this.sticky
      );

      this.attr(
        this.root,
        'data-pcu-sticky',
        'true'
      );
    }

    syncSticky() {
      if (!this.sticky) {
        return;
      }

      const native =
        this.nativeCTA();

      // Keep genuine preorder, sold-out and loading wording. No extra UI.
      const text =
        native
          ? cleanText(
              native.querySelector(
                '.btn-text'
              ) ||
              native
            )
          : 'Add to cart';

      if (
        this.stickyCTA
          .textContent !==
        text
      ) {
        this.stickyCTA.textContent =
          text;
      }

      this.stickyCTA.disabled =
        this.ctaBlocked(
          native
        );

      this.stickyCTA.setAttribute(
        'aria-busy',
        native?.getAttribute(
          'aria-busy'
        ) ||
        'false'
      );
    }

    scheduleView() {
      if (
        !this.root ||
        this.viewFrame
      ) {
        return;
      }

      this.viewFrame =
        requestAnimationFrame(
          () => {
            this.viewFrame = 0;
            this.updateView();
          }
        );
    }

    updateView() {
      if (!this.root) {
        return;
      }

      if (this.sticky) {
        const native =
          this.nativeCTA();

        const rect =
          native
            ?.getBoundingClientRect();

        const infoRect =
          this.info
            .getBoundingClientRect();

        const top =
          matchMedia(
            '(min-width: 1024px)'
          ).matches
            ? numberSetting(
                this.dataset
                  .headerOffset,
                80,
                0,
                160
              )
            : 0;

        // Also respect clipping by the desktop independently scrolling info rail.
        const clippedAbove =
          rect &&
          rect.bottom <=
            Math.max(
              top,
              infoRect.top
            );

        const drawer =
          document.getElementById(
            'CartDrawer'
          );

        const modalOpen =
          document.querySelector(
            'dialog[open], x-modal[active], x-modal[open]'
          );

        const drawerOpen =
          drawer &&
          !drawer.hidden &&
          (
            drawer.hasAttribute(
              'active'
            ) ||
            drawer.hasAttribute(
              'open'
            ) ||
            isVisible(
              drawer
            )
          );

        this.sticky.hidden =
          !native ||
          !clippedAbove ||
          Boolean(
            drawerOpen ||
            modalOpen
          );
      }

      this.updateFullWidth();
      this.updateGallery();
    }

    buildComplementary() {
      const nodes = [
        ...this.root.querySelectorAll(
          '[data-picco-style-with-root]'
        )
      ];

      if (!nodes.length) {
        return;
      }

      const desktop =
        matchMedia(
          '(min-width: 1024px)'
        ).matches;

      if (desktop) {
        // Restore the original recommendation node inside product-info on desktop.
        // Its listeners, quick-add and loading state are not cloned or rebuilt.
        nodes.forEach(
          (node) => {
            const anchor =
              this.moves.get(
                node
              );

            if (
              anchor?.isConnected &&
              this.info.contains(
                anchor
              )
            ) {
              if (
                node.parentNode !==
                  anchor.parentNode ||
                anchor.nextSibling !==
                  node
              ) {
                anchor.after(
                  node
                );
              }
            } else if (
              !this.info.contains(
                node
              )
            ) {
              this.info.append(
                node
              );
            }

            this.attr(
              node,
              'data-pcu-order',
              ''
            );

            this.style(
              node,
              '--pcu-order',
              '100'
            );
          }
        );

        if (
          this.fullWidth
        ) {
          this.fullWidth.hidden =
            true;
        }

        return;
      }

      if (
        !this.fullWidth
          ?.isConnected
      ) {
        this.fullWidth =
          this.own(
            element(
              'div',
              'pcu-complementary-fullwidth'
            )
          );

        this.root.append(
          this.fullWidth
        );
      }

      this.fullWidth.hidden =
        false;

      nodes.forEach(
        (node) =>
          this.moveNode(
            node,
            this.fullWidth
          )
      );

      this.updateFullWidth();
    }

    updateFullWidth() {
      if (
        !this.fullWidth
          ?.isConnected ||
        this.fullWidth.hidden
      ) {
        return;
      }

      // Use the layout viewport, excluding the scrollbar, not 100vw.
      const width =
        document.documentElement
          .clientWidth;

      this.fullWidth.style
        .setProperty(
          '--pcu-fullbleed-width',
          `${width}px`
        );

      const previous =
        parseFloat(
          this.fullWidth.style
            .getPropertyValue(
              '--pcu-fullbleed-shift'
            )
        ) ||
        0;

      const shift =
        previous -
        this.fullWidth
          .getBoundingClientRect()
          .left;

      if (
        Math.abs(
          previous -
          shift
        ) > .25
      ) {
        this.fullWidth.style
          .setProperty(
            '--pcu-fullbleed-shift',
            `${shift}px`
          );
      }
    }

    gallerySlides() {
      if (
        !this.galleryTrack
      ) {
        return [];
      }

      return [
        ...this.galleryTrack
          .querySelectorAll(
            '.product__media'
          )
      ].filter(
        (node) => {
          if (
            node.closest(
              'template'
            ) ||
            node.closest(
              '.product__preview'
            )
          ) {
            return false;
          }

          const box =
            node.getBoundingClientRect();

          return (
            box.width > 1 &&
            box.height > 1 &&
            getComputedStyle(
              node
            ).display !==
              'none'
          );
        }
      );
    }

    galleryMode() {
      if (
        !this.galleryTrack
      ) {
        return 'scroll';
      }

      // A managed carousel may initialize after the section is visible. Do not
      // impose a second scroll engine on its generated wrapper or transforms.
      const managed =
        this.galleryHost
          .querySelector(
            '.flickity-viewport, .swiper-wrapper, .splide__track'
          );

      const transform =
        getComputedStyle(
          this.galleryTrack
        ).transform;

      return (
        managed ||
        (
          transform &&
          transform !==
            'none' &&
          transform !==
            'matrix(1, 0, 0, 1, 0, 0)'
        )
      )
        ? 'managed'
        : 'scroll';
    }

    buildGallery() {
      const gallery =
        this.root.querySelector(
          '.native-mobile-gallery'
        );

      const track =
        gallery?.querySelector(
          '.product__media-list'
        );

      if (
        !gallery ||
        !track
      ) {
        return;
      }

      const host =
        track.closest(
          'slider-element'
        ) ||
        track.parentElement;

      if (
        this.galleryTrack !==
          track ||
        this.galleryHost !==
          host
      ) {
        this.galleryLife
          ?.abort();

        this.galleryObserver
          ?.disconnect();

        this.galleryResize
          ?.disconnect();

        this.galleryLife =
          new AbortController();

        this.gallery =
          gallery;

        this.galleryTrack =
          track;

        this.galleryHost =
          host;

        this.attr(
          gallery,
          'data-pcu-gallery',
          ''
        );

        this.attr(
          host,
          'data-pcu-gallery-host',
          ''
        );

        const listen = (
          node,
          name,
          handler,
          options = {}
        ) =>
          node.addEventListener(
            name,
            handler,
            {
              ...options,
              signal:
                this.galleryLife
                  .signal
            }
          );

        // Scroll does not bubble. Listen directly to the real media list and
        // capture descendant scrolls, including later native carousel wrappers.
        listen(
          track,
          'scroll',
          () =>
            this.scheduleView(),
          {
            passive: true
          }
        );

        listen(
          gallery,
          'scroll',
          () =>
            this.scheduleView(),
          {
            capture: true,
            passive: true
          }
        );

        listen(
          track,
          'scrollend',
          () =>
            this.scheduleView()
        );

        [
          'pointerdown',
          'pointermove',
          'pointerup',
          'touchend',
          'transitionrun',
          'transitionend'
        ].forEach(
          (name) =>
            listen(
              host,
              name,
              () =>
                this.watchGalleryMotion(),
              {
                passive: true
              }
            )
        );

        [
          'slideChanged',
          'slide:change',
          'slider:change',
          'carousel:change',
          'change',
          'select',
          'settle'
        ].forEach(
          (name) =>
            listen(
              gallery,
              name,
              () =>
                this.watchGalleryMotion(),
              {
                capture: true
              }
            )
        );

        listen(
          gallery,
          'click',
          (event) => {
            const arrow =
              event.target.closest?.(
                '[is="next-button"], [is="previous-button"]'
              );

            if (
              !arrow ||
              this.galleryMode() !==
                'scroll' ||
              innerWidth >= 1024
            ) {
              return;
            }

            if (
              arrow.getAttribute(
                'aria-controls'
              ) !== host.id
            ) {
              return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            this.goToGallerySlide(
              (
                this.galleryIndex ||
                0
              ) +
              (
                arrow.getAttribute(
                  'is'
                ) ===
                'next-button'
                  ? 1
                  : -1
              )
            );
          },
          {
            capture: true
          }
        );

        this.galleryObserver =
          new MutationObserver(
            (records) => {
              const relevant =
                records.filter(
                  (record) =>
                    !record.target
                      .closest?.(
                        '[data-pcu-model-caption]'
                      )
                );

              if (
                !relevant.length
              ) {
                return;
              }

              if (
                relevant.some(
                  (record) =>
                    record.type ===
                    'childList'
                )
              ) {
                this.scheduleRefresh();
              }

              this.watchGalleryMotion();
            }
          );

        this.galleryObserver.observe(
          host,
          {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: [
              'style',
              'class',
              'hidden',
              'aria-hidden'
            ]
          }
        );

        this.galleryResize =
          typeof ResizeObserver ===
            'function'
            ? new ResizeObserver(
                () =>
                  this.scheduleView()
              )
            : null;

        this.galleryResize?.observe(
          host
        );

        this.galleryResize?.observe(
          track
        );
      }

      this.attr(
        gallery,
        'data-pcu-gallery-mode',
        this.galleryMode()
      );

      this.placeModelCaption();

      if (
        this.dataset.progress ===
          'true' &&
        !this.progress
          ?.isConnected
      ) {
        this.progress =
          this.own(
            element(
              'div',
              'pcu-progress'
            )
          );

        this.progress.setAttribute(
          'role',
          'progressbar'
        );

        this.progress.setAttribute(
          'aria-label',
          'Product gallery position'
        );

        this.progress.setAttribute(
          'aria-valuemin',
          '1'
        );

        this.progress.append(
          element('span')
        );

        gallery.append(
          this.progress
        );
      }

      this.updateGallery();
    }

    placeModelCaption() {
      // Reuse the store's own text; never manufacture height, size or fit claims.
      const fresh =
        this.root.querySelector(
          '.model-info:not([data-pcu-model-caption])'
        );

      if (fresh) {
        this.modelCaption =
          fresh;
      }

      // A track-only rerender can detach the moved caption while its original
      // anchor remains in the gallery. Reattach that same node to the new slide.
      if (
        !this.modelCaption
      ) {
        this.modelCaption =
          this.root.querySelector(
            '.model-info'
          );
      }

      if (
        !this.modelCaption
      ) {
        return;
      }

      const first =
        innerWidth >= 1024
          ? this.root.querySelector(
              '.custom-main-images .main-img-wrap'
            )
          : this.galleryTrack
              .querySelector(
                '.product__media'
              );

      if (!first) return;

      this.moveNode(
        this.modelCaption,
        first
      );

      this.attr(
        this.modelCaption,
        'data-pcu-model-caption',
        ''
      );

      this.attr(
        this.modelCaption,
        'data-pcu-caption-hidden',
        'false'
      );
    }

    goToGallerySlide(
      index
    ) {
      const slides =
        this.gallerySlides();

      const target =
        slides[
          Math.max(
            0,
            Math.min(
              slides.length -
                1,
              index
            )
          )
        ];

      if (
        !target ||
        this.galleryMode() !==
          'scroll'
      ) {
        return;
      }

      const frame =
        this.galleryTrack
          .getBoundingClientRect();

      const box =
        target.getBoundingClientRect();

      this.galleryTrack.scrollBy(
        {
          left:
            box.left +
            box.width /
              2 -
            frame.left -
            frame.width /
              2,
          behavior:
            matchMedia(
              '(prefers-reduced-motion: reduce)'
            ).matches
              ? 'instant'
              : 'smooth'
        }
      );

      this.watchGalleryMotion();
    }

    watchGalleryMotion() {
      this.galleryMotionUntil =
        performance.now() +
        900;

      if (
        this.galleryMotionFrame
      ) {
        return;
      }

      const tick = () => {
        this.galleryMotionFrame =
          0;

        if (
          !this.root
            ?.isConnected
        ) {
          return;
        }

        this.updateGallery();

        if (
          performance.now() <
          this.galleryMotionUntil
        ) {
          this.galleryMotionFrame =
            requestAnimationFrame(
              tick
            );
        }
      };

      this.galleryMotionFrame =
        requestAnimationFrame(
          tick
        );
    }

    updateGallery() {
      if (
        !this.galleryTrack
          ?.isConnected
      ) {
        return;
      }

      if (
        innerWidth >= 1024
      ) {
        if (
          this.modelCaption
        ) {
          this.attr(
            this.modelCaption,
            'data-pcu-caption-hidden',
            'false'
          );
        }

        return;
      }

      const mode =
        this.galleryMode();

      this.attr(
        this.gallery,
        'data-pcu-gallery-mode',
        mode
      );

      const slides =
        this.gallerySlides();

      if (!slides.length) {
        if (this.progress) {
          this.progress.hidden =
            true;
        }

        return;
      }

      // Measure against the stationary viewport, not a track that may translate.
      const viewport =
        this.galleryHost.querySelector(
          '.flickity-viewport, .swiper, .splide__track'
        ) ||
        this.galleryHost;

      const frame =
        viewport.getBoundingClientRect();

      const center =
        (
          Math.max(
            frame.left,
            0
          ) +
          Math.min(
            frame.right,
            document.documentElement
              .clientWidth
          )
        ) /
        2;

      const centers =
        slides.map(
          (slide) => {
            const box =
              slide.getBoundingClientRect();

            return (
              box.left +
              box.width /
                2
            );
          }
        );

      let active =
        centers.reduce(
          (
            best,
            value,
            index
          ) =>
            Math.abs(
              value -
              center
            ) <
            Math.abs(
              centers[
                best
              ] -
              center
            )
              ? index
              : best,
          0
        );

      let position =
        active;

      // Interpolate between adjacent media for smooth progress during a swipe.
      for (
        let i = 0;
        i <
        centers.length -
          1;
        i += 1
      ) {
        const delta =
          centers[
            i + 1
          ] -
          centers[i];

        if (
          Math.abs(
            delta
          ) < 1
        ) {
          continue;
        }

        const fraction =
          (
            center -
            centers[i]
          ) /
          delta;

        if (
          fraction >= 0 &&
          fraction <= 1
        ) {
          position =
            i +
            fraction;

          break;
        }
      }

      this.galleryIndex =
        active;

      if (
        this.modelCaption
      ) {
        const first =
          this.galleryTrack
            .querySelector(
              '.product__media'
            );

        this.attr(
          this.modelCaption,
          'data-pcu-caption-hidden',
          String(
            slides[active] !==
            first
          )
        );
      }

      if (
        !this.progress
          ?.isConnected
      ) {
        return;
      }

      this.progress.hidden =
        slides.length < 2;

      this.progress.style
        .setProperty(
          '--pcu-progress-width',
          `${100 / slides.length}%`
        );

      this.progress.style
        .setProperty(
          '--pcu-progress-offset',
          `${position * 100}%`
        );

      this.progress.setAttribute(
        'aria-valuemax',
        String(
          slides.length
        )
      );

      this.progress.setAttribute(
        'aria-valuenow',
        String(
          active +
          1
        )
      );

      this.progress.setAttribute(
        'aria-valuetext',
        `Image or video ${
          active + 1
        } of ${
          slides.length
        }`
      );
    }

    buildZoomTriggers() {
      this.root
        .querySelectorAll(
          '.custom-main-images .main-img-wrap'
        )
        .forEach(
          (
            wrap,
            index
          ) => {
            const image =
              wrap.querySelector(
                'img'
              );

            if (
              !image ||
              wrap.querySelector(
                'video, model-viewer, iframe'
              ) ||
              wrap.querySelector(
                '.pcu-zoom-trigger'
              )
            ) {
              return;
            }

            const trigger =
              this.own(
                button(
                  'pcu-zoom-trigger'
                )
              );

            trigger.setAttribute(
              'aria-label',
              `Enlarge ${
                image.alt ||
                `product image ${
                  index +
                  1
                }`
              }`
            );

            wrap.append(
              trigger
            );
          }
        );
    }

    makeDialog(
      title,
      extraClass = ''
    ) {
      const dialog =
        this.own(
          element(
            'dialog',
            `pcu-dialog ${extraClass}`.trim()
          )
        );

      const header =
        element(
          'div',
          'pcu-dialog__header'
        );

      const heading =
        element(
          'h2',
          '',
          title
        );

      heading.id =
        `${this.uid}-dialog-${this.owned.size}`;

      dialog.setAttribute(
        'aria-labelledby',
        heading.id
      );

      const close =
        button(
          'pcu-dialog__close',
          'Close'
        );

      close.setAttribute(
        'aria-label',
        `Close ${title.toLowerCase()}`
      );

      close.autofocus =
        true;

      header.append(
        heading,
        close
      );

      const body =
        element(
          'div',
          'pcu-dialog__body'
        );

      dialog.append(
        header,
        body
      );

      this.on(
        close,
        'click',
        () =>
          dialog.close()
      );

      this.on(
        dialog,
        'click',
        (event) => {
          if (
            event.target !==
            dialog
          ) {
            return;
          }

          const rect =
            dialog.getBoundingClientRect();

          if (
            event.clientX <
              rect.left ||
            event.clientX >
              rect.right ||
            event.clientY <
              rect.top ||
            event.clientY >
              rect.bottom
          ) {
            dialog.close();
          }
        }
      );

      document.body.append(
        dialog
      );

      return {
        dialog,
        body,
        heading,
        close
      };
    }

    openZoom(wrap) {
      const images = [
        ...this.root.querySelectorAll(
          '.custom-main-images .main-img-wrap'
        )
      ].filter(
        (node) =>
          node.querySelector(
            'img'
          ) &&
          !node.querySelector(
            'video, model-viewer, iframe'
          )
      );

      if (!images.length) {
        return;
      }

      if (!this.zoom) {
        this.zoom =
          this.makeDialog(
            'Product image',
            'pcu-dialog--zoom'
          );

        this.zoomStage =
          element(
            'div',
            'pcu-zoom-stage'
          );

        this.zoomImage =
          element('img');

        this.zoomStage.append(
          this.zoomImage
        );

        const controls =
          element(
            'div',
            'pcu-zoom-controls'
          );

        const previous =
          button(
            'pcu-dialog__control',
            'Previous'
          );

        const next =
          button(
            'pcu-dialog__control',
            'Next'
          );

        this.zoomToggle =
          button(
            'pcu-dialog__control',
            'Zoom in'
          );

        const render = (
          move = 0
        ) => {
          this.zoomIndex =
            (
              this.zoomIndex +
              move +
              this.zoomImages
                .length
            ) %
            this.zoomImages
              .length;

          const source =
            this.zoomImages[
              this.zoomIndex
            ].querySelector(
              'img'
            );

          const nativeMedia = [
            ...this.root.querySelectorAll(
              '.native-mobile-gallery [data-media-id]'
            )
          ].find(
            (node) =>
              this.zoomImages[
                this.zoomIndex
              ].id.endsWith(
                node.dataset
                  .mediaId
                  .split('-')
                  .pop()
              )
          );

          const nativeImage =
            nativeMedia
              ?.querySelector(
                'img'
              );

          this.zoomImage.src =
            nativeImage
              ?.dataset
              .highRes ||
            source.dataset
              .highRes ||
            source.currentSrc ||
            source.src;

          this.zoomImage.alt =
            source.alt ||
            'Product image';

          this.zoom.heading.textContent =
            `Product image ${
              this.zoomIndex +
              1
            } of ${
              this.zoomImages
                .length
            }`;

          this.zoomStage
            .classList
            .remove(
              'is-enlarged'
            );

          this.zoomToggle.textContent =
            'Zoom in';

          this.zoomStage.scrollTop =
            0;

          this.zoomStage.scrollLeft =
            0;

          previous.disabled =
            this.zoomImages
              .length < 2;

          next.disabled =
            this.zoomImages
              .length < 2;
        };

        this.renderZoom =
          render;

        const toggle = () => {
          const enlarged =
            this.zoomStage
              .classList
              .toggle(
                'is-enlarged'
              );

          this.zoomToggle.textContent =
            enlarged
              ? 'Zoom out'
              : 'Zoom in';
        };

        this.on(
          previous,
          'click',
          () =>
            render(-1)
        );

        this.on(
          next,
          'click',
          () =>
            render(1)
        );

        this.on(
          this.zoomToggle,
          'click',
          toggle
        );

        this.on(
          this.zoomImage,
          'click',
          toggle
        );

        this.on(
          this.zoom.dialog,
          'keydown',
          (event) => {
            if (
              ![
                'ArrowLeft',
                'ArrowRight'
              ].includes(
                event.key
              )
            ) {
              return;
            }

            event.preventDefault();

            render(
              event.key ===
                'ArrowLeft'
                ? -1
                : 1
            );
          }
        );

        controls.append(
          previous,
          this.zoomToggle,
          next
        );

        this.zoom.body
          .replaceChildren(
            this.zoomStage,
            controls
          );
      }

      this.zoomImages =
        images;

      this.zoomIndex =
        Math.max(
          0,
          images.indexOf(
            wrap
          )
        );

      this.renderZoom();

      this.zoom.dialog
        .showModal();
    }

    async openQuickShop(
      trigger
    ) {
      let productURL;

      try {
        productURL =
          sameOriginURL(
            trigger.dataset
              .productUrl
          );
      } catch (_) {
        return;
      }

      this.quickRequest
        ?.abort();

      this.quickRequest =
        new AbortController();

      const request =
        this.quickRequest;

      if (!this.quick) {
        this.quick =
          this.makeDialog(
            'Quick shop'
          );

        this.on(
          this.quick.dialog,
          'close',
          () => {
            this.quickRequest
              ?.abort();

            this.attr(
              this.quickTrigger,
              'aria-expanded',
              'false'
            );

            this.quickTrigger
              ?.focus();
          }
        );
      }

      this.attr(
        this.quickTrigger,
        'aria-expanded',
        'false'
      );

      this.quickTrigger =
        trigger;

      this.attr(
        trigger,
        'aria-expanded',
        'true'
      );

      this.quick.body
        .replaceChildren(
          element(
            'p',
            'pcu-status',
            'Loading product...'
          )
        );

      this.quick.dialog
        .showModal();

      try {
        // Correct: /products/name.js?tracking=... NOT /products/name?tracking=....js
        const jsonURL =
          new URL(
            productURL
          );

        jsonURL.pathname =
          `${jsonURL.pathname
            .replace(
              /\/$/,
              ''
            )
            .replace(
              /\.js$/,
              ''
            )}.js`;

        jsonURL.hash = '';

        const response =
          await fetch(
            jsonURL.href,
            {
              credentials:
                'same-origin',
              headers: {
                Accept:
                  'application/json'
              },
              signal:
                request.signal
            }
          );

        if (!response.ok) {
          throw new Error(
            'Unable to load this product.'
          );
        }

        const product =
          await response.json();

        if (
          request.signal
            .aborted ||
          !this.quick?.dialog
            .open
        ) {
          return;
        }

        if (
          !Array.isArray(
            product.variants
          ) ||
          !product.variants
            .length
        ) {
          throw new Error(
            'No product options were returned.'
          );
        }

        this.renderQuickShop(
          product,
          productURL
        );
      } catch (error) {
        if (
          error.name ===
          'AbortError'
        ) {
          return;
        }

        this.quick.body
          .replaceChildren(
            element(
              'p',
              'pcu-status pcu-error',
              'This product could not be loaded here. Please use its product page.'
            )
          );

        const link =
          element(
            'a',
            'pcu-button',
            'Open product page'
          );

        link.href =
          productURL.href;

        this.quick.body.append(
          link
        );
      }
    }

    renderQuickShop(
      product,
      productURL
    ) {
      const grid =
        element(
          'div',
          'pcu-quickshop'
        );

      const image =
        element('img');

      image.alt =
        product.title ||
        'Product';

      const info =
        element('div');

      const title =
        element(
          'h3',
          '',
          product.title ||
          'Product'
        );

      const price =
        element('p');

      const status =
        element(
          'p',
          'pcu-status'
        );

      status.setAttribute(
        'role',
        'status'
      );

      status.setAttribute(
        'aria-live',
        'polite'
      );

      const add =
        button(
          'pcu-button',
          'Add to cart'
        );

      const pageLink =
        element(
          'a',
          '',
          'Full product details'
        );

      const initial =
        product.variants.find(
          (variant) =>
            variant.available
        ) ||
        product.variants[0];

      const selectors = [];

      info.append(
        title,
        price
      );

      (
        product.options ||
        []
      ).forEach(
        (
          option,
          index
        ) => {
          const name =
            typeof option ===
              'string'
              ? option
              : option.name;

          const label =
            element(
              'label',
              '',
              name ||
              `Option ${
                index +
                1
              }`
            );

          const select =
            element(
              'select'
            );

          const values = [
            ...new Set(
              product.variants
                .map(
                  (variant) =>
                    variant
                      .options?.[
                      index
                    ]
                )
                .filter(
                  (value) =>
                    value != null
                )
            )
          ];

          values.forEach(
            (value) => {
              const entry =
                element(
                  'option',
                  '',
                  value
                );

              entry.value =
                value;

              select.append(
                entry
              );
            }
          );

          select.value =
            initial.options
              ?.[index] ||
            values[0] ||
            '';

          selectors.push(
            select
          );

          label.append(
            select
          );

          info.append(
            label
          );
        }
      );

      // A quick-add JSON response cannot reproduce all app-managed purchase UI.
      // Products advertising plans/preorders use the actual product page instead.
      const requiresNative =
        Boolean(
          product.requires_selling_plan ||
          product.selling_plan_groups
            ?.length ||
          product.variants.length >=
            250 ||
          product.variants.some(
            (variant) =>
              variant.requires_selling_plan ||
              variant.selling_plan_allocations
                ?.length
          ) ||
          /pre[\s-]?order|subscription|personalised|personalized|customis|customiz/i.test(
            `${
              product.title ||
              ''
            } ${
              product.handle ||
              ''
            } ${
              String(
                product.tags ||
                ''
              )
            }`
          )
        );

      const choose = () => {
        const selected =
          selectors.map(
            (select) =>
              select.value
          );

        return (
          product.variants.find(
            (variant) =>
              variant.options
                ?.length ===
                selected.length &&
              variant.options
                .every(
                  (
                    value,
                    index
                  ) =>
                    value ===
                    selected[index]
                )
          ) ||
          (
            !selected.length
              ? product
                  .variants[0]
              : null
          )
        );
      };

      const render = () => {
        const variant =
          choose();

        const target =
          new URL(
            productURL
          );

        if (variant) {
          target.searchParams.set(
            'variant',
            String(
              variant.id
            )
          );
        }

        pageLink.href =
          target.href;

        price.textContent =
          variant
            ? money(
                variant.price,
                this.dataset
                  .currency ||
                window.Shopify
                  ?.currency
                  ?.active
              )
            : 'Unavailable combination';

        const source =
          variant
            ?.featured_image
            ?.src ||
          product.featured_image ||
          product.images
            ?.[0];

        if (source) {
          image.src =
            source;

          image.hidden =
            false;
        } else {
          image.hidden =
            true;
        }

        add.disabled =
          requiresNative
            ? !variant
            : !variant
                ?.available;

        add.textContent =
          requiresNative
            ? 'Choose on product page'
            : variant
                ?.available
              ? 'Add to cart'
              : 'Sold out';

        status.textContent =
          requiresNative
            ? 'Complete this purchase on its product page so preorder terms and any app options are included.'
            : '';

        return {
          variant,
          target
        };
      };

      selectors.forEach(
        (select) =>
          this.on(
            select,
            'change',
            render
          )
      );

      this.on(
        add,
        'click',
        async () => {
          const {
            variant,
            target
          } = render();

          if (!variant) {
            return;
          }

          if (
            requiresNative
          ) {
            location.assign(
              target.href
            );
            return;
          }

          if (
            !variant.available ||
            add.dataset.busy ===
              'true'
          ) {
            return;
          }

          add.dataset.busy =
            'true';

          add.disabled =
            true;

          add.textContent =
            'Adding...';

          selectors.forEach(
            (select) => {
              select.disabled =
                true;
            }
          );

          status.textContent =
            '';

          let sent = false;
          let accepted = false;

          try {
            const root =
              sameOriginURL(
                this.dataset
                  .rootUrl ||
                window.Shopify
                  ?.routes
                  ?.root ||
                '/'
              );

            const addURL =
              new URL(
                `${root.pathname.replace(
                  /\/?$/,
                  '/'
                )}cart/add.js`,
                root.origin
              );

            const body =
              new FormData();

            body.set(
              'id',
              String(
                variant.id
              )
            );

            body.set(
              'quantity',
              '1'
            );

            sent = true;

            // Deliberately not aborted on dialog close. A cart POST might already
            // have committed; silently retrying it could add a duplicate item.
            const response =
              await fetch(
                addURL.href,
                {
                  method: 'POST',
                  credentials:
                    'same-origin',
                  headers: {
                    Accept:
                      'application/json'
                  },
                  body
                }
              );

            if (
              !response.ok
            ) {
              sent = false;

              const result =
                await response
                  .json()
                  .catch(
                    () => ({})
                  );

              throw new Error(
                typeof result.description ===
                  'string'
                  ? result.description
                  : 'The item could not be added.'
              );
            }

            accepted = true;

            status.textContent =
              'Added. Opening your cart...';

            // Concept's documented event refreshes its cart contents and opens it.
            // Close the modal first; a top-layer dialog would cover the drawer.
            if (
              !this.openCartDrawer()
            ) {
              status.textContent =
                'Added to cart. The cart drawer is not enabled on this page.';

              add.hidden =
                true;

              const cart =
                element(
                  'a',
                  'pcu-button',
                  'View cart'
                );

              cart.href =
                sameOriginURL(
                  this.dataset
                    .cartUrl ||
                  '/cart'
                ).href;

              status.after(
                cart
              );
            }
          } catch (error) {
            const uncertain =
              sent ||
              accepted;

            status.textContent =
              uncertain
                ? 'The connection was interrupted. Check your cart before adding again.'
                : error.message;

            status.classList.add(
              'pcu-error'
            );

            if (uncertain) {
              add.hidden =
                true;

              const cart =
                element(
                  'a',
                  'pcu-button',
                  'Check cart'
                );

              cart.href =
                sameOriginURL(
                  this.dataset
                    .cartUrl ||
                  '/cart'
                ).href;

              status.after(
                cart
              );
            } else {
              add.dataset.busy =
                'false';

              add.disabled =
                false;

              add.textContent =
                'Try again';

              selectors.forEach(
                (select) => {
                  select.disabled =
                    false;
                }
              );
            }
          }
        }
      );

      info.append(
        add,
        pageLink,
        status
      );

      grid.append(
        image,
        info
      );

      this.quick.body
        .replaceChildren(
          grid
        );

      render();
    }

    teardownProduct() {
      this.cartUI
        ?.owners
        .delete(
          this
        );

      this.cartUI = null;

      this.productObserver
        ?.disconnect();

      this.resizeObserver
        ?.disconnect();

      this.drawerObserver
        ?.disconnect();

      this.galleryObserver
        ?.disconnect();

      this.galleryResize
        ?.disconnect();

      this.galleryLife
        ?.abort();

      this.productLife
        ?.abort();

      this.quickRequest
        ?.abort();

      clearTimeout(
        this.pendingTimer
      );

      clearTimeout(
        this.errorTimer
      );

      cancelAnimationFrame(
        this.frame
      );

      cancelAnimationFrame(
        this.viewFrame
      );

      cancelAnimationFrame(
        this.galleryMotionFrame
      );

      this.galleryMotionFrame =
        0;

      this.frame = 0;
      this.viewFrame = 0;

      this.resetTabs();
      this.restoreMoves();

      for (
        const node of
        this.owned
      ) {
        if (
          node.tagName ===
            'DIALOG' &&
          node.open
        ) {
          node.close();
        }

        node.remove();
      }

      this.owned.clear();

      for (
        const node of [
          ...this.records.keys()
        ]
      ) {
        this.restoreNode(
          node
        );
      }

      this.root = null;
      this.info = null;
      this.form = null;
      this.sticky = null;
      this.progress = null;
      this.progressTrack = null;

      this.quick = null;
      this.zoom = null;

      this.fullWidth = null;
      this.gallery = null;
      this.galleryHost = null;
      this.galleryTrack = null;
      this.modelCaption = null;

      this.optionSignature =
        null;

      this.priceSignature =
        null;

      this.stickyPending =
        false;

      this.variantFailed =
        false;

      this.cartIntent =
        null;

      this.stockStatus =
        null;

      this.sizeGrids?.clear();

      this.localAttempted =
        false;

      this.localMode =
        false;

      this.notifyingLocal =
        false;

      this.localById =
        null;

      this.localByOptions =
        null;

      this.localValues =
        null;

      this.localEntry =
        null;

      this.livePrice =
        null;

      this.planStatus =
        null;

      this.localPlanIssue =
        null;

      this.purchaseSignature =
        null;

      this.timesactPlanSeen =
        false;
    }
  }

  customElements.define(
    'picco-pdp-upgrade',
    PiccoProductUpgrade
  );
})();